import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';
import { useIdentityReveal } from '../../contexts/IdentityRevealContext';
import { useAuthFetch, QuotaExceededError } from '../../hooks/useAuthFetch';
import { useTagConflictModal } from '../../hooks/useTagConflictModal';
import { useRealtimeResumes } from '../../hooks/useRealtimeResumes';
import { canEditItem, isOrgAdmin, ownerLabel } from '../../utils/ownership';
import { formatSkillLabel, isSkillExplicitInResume } from '../../utils/skillDisplay';
import { previewTagAddition, extractTagErrors, formatConflictMessages, MAX_TAG_LENGTH } from '../../utils/tagIntelligenceApi';
import {
  DEFAULT_RADAR_DIMENSION_WEIGHTS,
  RADAR_DIMENSION_KEYS,
  RadarDimensionKey,
  getRadarDimensionLabel,
} from '../../utils/radarDimensions';
import TransferOwnershipModal from '../../components/TransferOwnershipModal';
import DimensionRadarChart from '../../components/charts/DimensionRadarChart';
import DimensionHeatmap from '../../components/charts/DimensionHeatmap';
import TagConflictAlert from '../../components/TagConflictAlert';
import {
  BULK_UPLOAD_MAX_FILES,
  DIRECT_UPLOAD_MAX_FILES,
  issuesToTooltip,
  toMb,
  validateResumeFiles,
} from '../../utils/uploadPolicy';
import html2canvas from 'html2canvas';
import './JobWorkspacePage.css';

/* ── Types ── */
interface JDDetail {
  id: string;
  title: string;
  text: string;
  source: string;
  createdAt: string;
  indexed: boolean;
  tags: string[];
  dimensionWeights: Record<string, number>;
  dimensionWeightsReasoning: Record<string, string>;
  dimensionWeightsReasoningDetails: Record<string, {
    rationale?: string;
    jdSignals?: string[];
    standards?: { onet?: string; shrm?: string };
    confidence?: number;
    riskIfOverweighted?: string;
  }>;
  weightsSource: string;
  jobStatus: 'open' | 'filled' | 'closed';
  closedAt: string;
  closedBy: string;
  filledResumeIds: string[];
  filledCandidates: { resumeId: string; filename: string }[];
  uploadedBy: string;
}

interface MatchRanking {
  resumeId: string;
  resumeFilename: string;
  candidateAlias?: string;
  displayName?: string;
  originalFilename?: string;
  score: number;
  rank: number;
  strengths: string[];
  improvements: string[];
  explanation: string;
  tagConflicts?: string[];
  dimensionScores?: Record<string, number>;
  dimensionExplanations?: Record<string, string>;
  hasScore?: boolean;
}

interface EnrichmentData {
  consolidatedSkills: string[];
  criticValidationPassed: boolean | null;
  criticSummary: string;
  dimensions: Record<string, { confidence: number; findings: string[]; reasoning: string | string[]; skills: string[]; inferred_skills?: string[] }>;
  enrichmentMode: string;
  resumeText: string;
}

const REPORT_HEATMAP_MAX_CANDIDATES = 50;
const UI_HEATMAP_MAX_CANDIDATES = 30;
const REPORT_HEATMAP_DIMENSION_KEYS = RADAR_DIMENSION_KEYS;

interface ReportHeatmapDimensionPayload {
  key: string;
  label: string;
  weight: number;
}

interface ReportHeatmapCandidatePayload {
  name: string;
  overallScore: number;
  dimensionScores: Record<string, number>;
  dimensionExplanations?: Record<string, string>;
}

interface ReportHeatmapPayload {
  orientation: 'candidate_rows';
  dimensions: ReportHeatmapDimensionPayload[];
  candidates: ReportHeatmapCandidatePayload[];
}

const DIMENSION_META: Record<string, { label: string; subtitle: string }> = {
  technical_skills: {
    label: getRadarDimensionLabel('technical_skills', 'full'),
    subtitle: 'Role-specific applied capabilities evidenced through tasks, outcomes, and delivery depth.',
  },
  experience_level: {
    label: getRadarDimensionLabel('experience_level', 'full'),
    subtitle: 'Depth of responsibility, operating scale, and complexity handled across prior roles.',
  },
  domain_expertise: {
    label: getRadarDimensionLabel('domain_expertise', 'full'),
    subtitle: 'Knowledge of industry context, business models, regulations, and domain-specific workflows.',
  },
  problem_solving: {
    label: getRadarDimensionLabel('problem_solving', 'full'),
    subtitle: 'Evidence of analytical reasoning, troubleshooting quality, and solution architecture judgment.',
  },
  tech_stack: {
    label: getRadarDimensionLabel('tech_stack', 'full'),
    subtitle: 'Breadth, depth, and recency of tools, platforms, and ecosystems used in production contexts.',
  },
  transferable_skills: {
    label: getRadarDimensionLabel('transferable_skills', 'full'),
    subtitle: 'Portable competencies that transfer across functions, teams, and problem domains.',
  },
  education_certs: {
    label: getRadarDimensionLabel('education_certs', 'full'),
    subtitle: 'Formal credentials, certifications, and demonstrated continuous learning velocity.',
  },
  collaboration_leadership: {
    label: getRadarDimensionLabel('collaboration_leadership', 'full'),
    subtitle: 'Work style indicators: communication, collaboration quality, ownership, and leadership behaviors.',
  },
};

const DEFAULT_CHART_DIMENSION_WEIGHTS: Record<string, number> = {
  ...DEFAULT_RADAR_DIMENSION_WEIGHTS,
};

const TIER_RANK: Record<string, number> = {
  free: 0,
  starter: 1,
  pro: 2,
  business: 3,
  enterprise: 4,
};

const INELIGIBLE_RESUME_STATUSES = new Set([
  'rejected_oversized',
  'failed',
  'upload_failed',
  'rejected',
]);

function isEligibleLinkedCandidate(status?: string): boolean {
  return !INELIGIBLE_RESUME_STATUSES.has(status ?? '');
}

function resolveChartDimensionWeights(weights?: Record<string, number>): Record<string, number> {
  if (!weights || Object.keys(weights).length === 0) {
    return { ...DEFAULT_CHART_DIMENSION_WEIGHTS };
  }

  const merged = { ...DEFAULT_CHART_DIMENSION_WEIGHTS };
  let hasPositive = false;

  Object.entries(weights).forEach(([key, value]) => {
    if (!(key in merged)) return;
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric < 0) return;
    merged[key] = numeric;
    if (numeric > 0) hasPositive = true;
  });

  if (!hasPositive) {
    return { ...DEFAULT_CHART_DIMENSION_WEIGHTS };
  }

  const total = Object.values(merged).reduce((sum, val) => sum + val, 0);
  if (total <= 0) {
    return { ...DEFAULT_CHART_DIMENSION_WEIGHTS };
  }

  const normalized: Record<string, number> = {};
  Object.entries(merged).forEach(([key, value]) => {
    normalized[key] = value / total;
  });
  return normalized;
}

type CandidateDirtyReason =
  | 'discover_linked'
  | 'link_set_changed'
  | 'resume_indexed'
  | 'jd_tags_changed'
  | 'jd_weights_changed'
  | 'manual_unlink';

/* ── Icons ── */
const IconArrowLeft = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
);
const IconSearch = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
);
const IconChevron = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
);

function scoreClass(score: number): string {
  if (score >= 70) return 'v2-score-high';
  if (score >= 45) return 'v2-score-medium';
  return 'v2-score-low';
}

function scoreDisplay(score: number, hasScore = true): string {
  return hasScore ? `${Math.round(score)}%` : '—';
}

function displayResumeName(resumeFilename?: string): string {
  return resumeFilename
    ?.split('/')
    .pop()
    ?.replace(/^default_user_\d+_/, '')
    .replace(/\.[^/.]+$/, '') || resumeFilename || 'Unknown Candidate';
}

function fallbackCandidateAlias(resumeId?: string): string {
  const token = (resumeId || '').replace(/[^a-zA-Z0-9]/g, '').slice(0, 8).toUpperCase();
  return token ? `Candidate ${token}` : 'Candidate Anonymous';
}

function displayCandidateName(
  ranking: Pick<MatchRanking, 'candidateAlias' | 'resumeId' | 'resumeFilename' | 'originalFilename'>,
  revealIdentity = false,
): string {
  if (revealIdentity) {
    const originalName = ranking.originalFilename || ranking.resumeFilename;
    if (originalName) return displayResumeName(originalName);
  }
  return ranking.candidateAlias || fallbackCandidateAlias(ranking.resumeId);
}

function buildEffectiveCandidateRankings(
  rankings: MatchRanking[],
  linkedResumes: {
    resumeId: string;
    filename: string;
    status?: string;
    candidateAlias?: string;
    displayName?: string;
    originalFilename?: string;
    inlineScores?: Record<string, { score: number; summary: string; scoredAt: string; dimensionScores?: Record<string, number>; dimensionExplanations?: Record<string, string> }>;
  }[],
  jobId: string,
  revealIdentity: boolean,
): MatchRanking[] {
  const linkedResumeIds = new Set(linkedResumes.map(r => r.resumeId));

  const serverById = new Map<string, MatchRanking>();
  rankings
    .filter(r => r.resumeId && linkedResumeIds.has(r.resumeId))
    .forEach(r => {
      serverById.set(r.resumeId, {
        ...r,
        resumeFilename: displayCandidateName(r, revealIdentity),
        hasScore: true,
      });
    });

  const inlineById = new Map<string, MatchRanking>();
  linkedResumes.forEach(r => {
    const inlineScore = r.inlineScores?.[jobId];
    if (!inlineScore) return;
    inlineById.set(r.resumeId, {
      resumeId: r.resumeId,
      resumeFilename: r.filename,
      score: inlineScore.score,
      rank: 0,
      strengths: [],
      improvements: [],
      explanation: inlineScore.summary,
      dimensionScores: inlineScore.dimensionScores,
      dimensionExplanations: inlineScore.dimensionExplanations,
      hasScore: true,
    });
  });

  const merged = linkedResumes.map((linked) => {
    const server = serverById.get(linked.resumeId);
    const inline = inlineById.get(linked.resumeId);
    const source = server || inline;
    if (source) {
      return {
        ...source,
        resumeId: linked.resumeId,
        resumeFilename: source.resumeFilename || linked.filename,
        candidateAlias: source.candidateAlias,
        displayName: source.displayName,
        originalFilename: source.originalFilename,
        score: Number.isFinite(source.score) ? source.score : 0,
        strengths: source.strengths || [],
        improvements: source.improvements || [],
        explanation: source.explanation || '',
        tagConflicts: source.tagConflicts,
        dimensionScores: source.dimensionScores,
        dimensionExplanations: source.dimensionExplanations,
        hasScore: true,
      };
    }

    return {
      resumeId: linked.resumeId,
      resumeFilename: linked.filename,
      candidateAlias: linked.candidateAlias,
      displayName: linked.displayName,
      originalFilename: linked.originalFilename,
      score: 0,
      rank: 0,
      strengths: [],
      improvements: [],
      explanation: linked.status === 'indexed' ? 'Scored results are not available yet.' : 'This resume is still processing.',
      dimensionScores: undefined,
      dimensionExplanations: undefined,
      hasScore: false,
    };
  });

  return merged
    .sort((a, b) => {
      if ((a.hasScore ? 1 : 0) !== (b.hasScore ? 1 : 0)) return (b.hasScore ? 1 : 0) - (a.hasScore ? 1 : 0);
      const scoreA = Number.isFinite(a.score) ? a.score : 0;
      const scoreB = Number.isFinite(b.score) ? b.score : 0;
      if (scoreA !== scoreB) return scoreB - scoreA;
      return a.resumeFilename.localeCompare(b.resumeFilename);
    })
    .map((r, i) => ({ ...r, rank: i + 1 }));
}

function buildReportHeatmapPayload<T extends { score: number; dimensionScores?: Record<string, number>; dimensionExplanations?: Record<string, string> }>(
  dimensionWeights: Record<string, number>,
  candidates: T[],
  resolveName: (candidate: T, index: number) => string,
): ReportHeatmapPayload | undefined {
  const normalizedWeights = resolveChartDimensionWeights(dimensionWeights);
  const dimensions = REPORT_HEATMAP_DIMENSION_KEYS.map(key => ({
    key,
    label: DIMENSION_META[key].label,
    weight: normalizedWeights[key] || 0,
  }));

  const heatmapCandidates = candidates
    .filter(candidate => candidate.dimensionScores && Object.keys(candidate.dimensionScores).length > 0)
    .slice(0, REPORT_HEATMAP_MAX_CANDIDATES)
    .map((candidate, index) => {
      const resolvedName = resolveName(candidate, index).trim() || `Candidate ${index + 1}`;
      const dimensionScores = REPORT_HEATMAP_DIMENSION_KEYS.reduce<Record<string, number>>((acc, key) => {
        const value = candidate.dimensionScores?.[key] ?? 0;
        acc[key] = Number.isFinite(value) ? value : 0;
        return acc;
      }, {});

      return {
        name: resolvedName,
        overallScore: Number.isFinite(candidate.score) ? candidate.score : 0,
        dimensionScores,
        dimensionExplanations: candidate.dimensionExplanations,
      };
    });

  if (heatmapCandidates.length === 0) return undefined;

  return {
    orientation: 'candidate_rows',
    dimensions,
    candidates: heatmapCandidates,
  };
}

// Reasons that require a new LLM scoring round (tag changes and unlinks are in-memory only)
const RESCORE_REASONS = new Set<CandidateDirtyReason>([
  'discover_linked',
  'link_set_changed',
  'resume_indexed',
  'jd_weights_changed',
]);

export const JobWorkspacePage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const authFetch = useAuthFetch();
  const { org, role } = useTenant();
  const { user } = useAuth();
  const { revealIdentity } = useIdentityReveal();
  const { resumes } = useRealtimeResumes(org?.orgId);

  const [tab, setTab] = useState<'overview' | 'candidates'>('overview');
  const [addCandidatesOpen, setAddCandidatesOpen] = useState(false);
  const [addCandidatesSection, setAddCandidatesSection] = useState<'upload' | 'talent'>('upload');
  const [addCandidatesStatus, setAddCandidatesStatus] = useState<string | null>(null);
  const [jd, setJd] = useState<JDDetail | null>(null);
  const prewarmMatchFired = useRef(false);

  const firePrewarmMatch = useCallback(() => {
    authFetch('/api/prewarm-match', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    }).catch(() => {});
  }, [authFetch]);

  const openAddCandidates = useCallback((section: 'upload' | 'talent' = 'upload') => {
    setAddCandidatesSection(section);
    setAddCandidatesOpen(true);
  }, []);

  const showAddCandidatesStatus = useCallback((message: string) => {
    setAddCandidatesStatus(message);
    window.setTimeout(() => setAddCandidatesStatus(null), 7000);
  }, []);

  // Pre-warm match on tab change
  useEffect(() => {
    if (tab === 'candidates') {
      if (!prewarmMatchFired.current) {
        prewarmMatchFired.current = true;
        firePrewarmMatch();
      }
    }
  }, [tab, firePrewarmMatch]);

  const [loading, setLoading] = useState(true);

  // Hydrate candidates from sessionStorage (stale-while-revalidate)
  const candidatesCacheKey = jobId ? `xrank_candidates_v2_${jobId}` : '';
  const cachedCandidates = useMemo(() => {
    if (!candidatesCacheKey) return null;
    try { const raw = sessionStorage.getItem(candidatesCacheKey); return raw ? JSON.parse(raw) : null; } catch { return null; }
  }, [candidatesCacheKey]);

  // Candidates tab state
  const [candidates, setCandidates] = useState<MatchRanking[]>(cachedCandidates?.rankings ?? []);
  const [candidatesFiltered, setCandidatesFiltered] = useState<MatchRanking[]>(cachedCandidates?.filtered ?? []);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidatesRan, setCandidatesRan] = useState(!!cachedCandidates);
  const [expanded, setExpanded] = useState<string | null>(null);

  // Discover tab state — no sessionStorage cache (on-demand search, stale data causes confusion)
  const [discoverResults, setDiscoverResults] = useState<MatchRanking[]>([]);
  const [discoverLoading, setDiscoverLoading] = useState(false);
  const [discoverRan, setDiscoverRan] = useState(false);
  const [discoverTopN, setDiscoverTopN] = useState(10);
  const [discoverApplyTagFilter, setDiscoverApplyTagFilter] = useState(false);
  const [titleEditMode, setTitleEditMode] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [titleSaving, setTitleSaving] = useState(false);
  const [showTransferOwnership, setShowTransferOwnership] = useState(false);
  const canUseTagFilter = useMemo(() => {
    const plan = (org?.plan || 'free').toLowerCase();
    return (TIER_RANK[plan] ?? 0) >= 2;
  }, [org?.plan]);

  useEffect(() => {
    if (canUseTagFilter) return;
    setDiscoverApplyTagFilter(false);
  }, [canUseTagFilter]);

  const [error, setError] = useState<string | null>(null);
  const [pendingLinkedResumeIds, setPendingLinkedResumeIds] = useState<Set<string>>(new Set());
  const [dirtyReasons, setDirtyReasons] = useState<Set<CandidateDirtyReason>>(new Set());
  const [dirtyTick, setDirtyTick] = useState(0);
  const prevLinkedIdsRef = useRef<Set<string> | null>(null);
  const prevLinkedStatusesRef = useRef<Map<string, string>>(new Map());
  const linkedSetHydratedRef = useRef(false);
  const scoreBackfillKeyRef = useRef<string>('');
  const detailHydrationKeyRef = useRef<string>('');
  const lastAutoRematchTickRef = useRef(0);
  const matchInFlightRef = useRef(false);
  const discoverInFlightRef = useRef(false);

  // Resumes linked to this JD
  const linkedResumes = useMemo(() => {
    if (!jobId) return [];
    return resumes.filter(r => (r.jdIds ?? []).includes(jobId));
  }, [resumes, jobId]);

  const eligibleLinkedResumes = useMemo(
    () => linkedResumes.filter(r => isEligibleLinkedCandidate(r.status)),
    [linkedResumes],
  );

  const linkedResumesForView = useMemo(() => {
    return eligibleLinkedResumes.map(r => {
      const displayFilename = revealIdentity
        ? (r.filename || r.candidateAlias || fallbackCandidateAlias(r.resumeId))
        : (r.candidateAlias || fallbackCandidateAlias(r.resumeId));
      return { ...r, filename: displayFilename };
    });
  }, [eligibleLinkedResumes, revealIdentity]);

  const linkedResumeIds = useMemo(() => new Set(eligibleLinkedResumes.map(r => r.resumeId)), [eligibleLinkedResumes]);
  const ineligibleResumeIds = useMemo(
    () => new Set(resumes.filter(r => !isEligibleLinkedCandidate(r.status)).map(r => r.resumeId)),
    [resumes],
  );
  const effectiveCandidateRankings = useMemo(
    () => buildEffectiveCandidateRankings(candidates, linkedResumesForView, jobId || '', revealIdentity),
    [candidates, linkedResumesForView, jobId, revealIdentity],
  );
  const missingInlineScoreIds = useMemo(() => {
    if (!jobId) return [] as string[];
    return linkedResumesForView
      .filter(r => !r.inlineScores?.[jobId] || !Number.isFinite(Number(r.inlineScores?.[jobId]?.score)))
      .map(r => r.resumeId)
      .filter(Boolean)
      .sort();
  }, [linkedResumesForView, jobId]);
  const missingDetailedAnalysisIds = useMemo(() => {
    return effectiveCandidateRankings
      .filter(r => {
        if (!r.resumeId) return false;
        if (r.hasScore === false) return false;
        const hasStrengths = (r.strengths?.length || 0) > 0;
        const hasImprovements = (r.improvements?.length || 0) > 0;
        const hasDimensionExplanations = !!r.dimensionExplanations && Object.keys(r.dimensionExplanations).length > 0;
        return !hasStrengths && !hasImprovements && !hasDimensionExplanations;
      })
      .map(r => r.resumeId)
      .filter(Boolean)
      .sort();
  }, [effectiveCandidateRankings]);
  const fillRankedCandidates = useMemo(
    () => effectiveCandidateRankings.map(r => ({
      resumeId: r.resumeId,
      displayName: displayCandidateName(r, revealIdentity),
      score: typeof r.score === 'number' ? r.score : null,
    })),
    [effectiveCandidateRankings, revealIdentity],
  );
  const chartDimensionWeights = useMemo(
    () => resolveChartDimensionWeights(jd?.dimensionWeights),
    [jd?.dimensionWeights],
  );

  const markDirty = useCallback((reason: CandidateDirtyReason) => {
    setDirtyReasons(prev => new Set(prev).add(reason));
    setDirtyTick(prev => prev + 1);
  }, []);

  useEffect(() => {
    if (pendingLinkedResumeIds.size === 0) return;
    const linkedIds = new Set(linkedResumes.map(r => r.resumeId));
    setPendingLinkedResumeIds(prev => {
      const next = new Set(Array.from(prev).filter(id => !linkedIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [linkedResumes, pendingLinkedResumeIds.size]);

  // When resumes get linked, do NOT remove them from discover — they stay with "Linked" badge

  useEffect(() => {
    const prev = prevLinkedIdsRef.current;
    if (!prev) {
      prevLinkedIdsRef.current = new Set(linkedResumeIds);
      return;
    }

    // First realtime hydration can transition from 0 -> N linked resumes.
    // Skip auto-rematch for this transition; existing inline/cache data renders immediately.
    if (!linkedSetHydratedRef.current) {
      linkedSetHydratedRef.current = true;
      prevLinkedIdsRef.current = new Set(linkedResumeIds);
      return;
    }

    const changed = prev.size !== linkedResumeIds.size || Array.from(linkedResumeIds).some(id => !prev.has(id));
    if (changed) markDirty('link_set_changed');
    prevLinkedIdsRef.current = new Set(linkedResumeIds);
  }, [linkedResumeIds, markDirty]);

  useEffect(() => {
    const current = new Map(linkedResumes.map(r => [r.resumeId, r.status]));
    const prev = prevLinkedStatusesRef.current;
    if (prev.size > 0) {
      const newlyIndexed = linkedResumes.some(r => prev.get(r.resumeId) && prev.get(r.resumeId) !== 'indexed' && r.status === 'indexed');
      if (newlyIndexed) markDirty('resume_indexed');
    }
    prevLinkedStatusesRef.current = current;
  }, [linkedResumes, markDirty]);

  const displayLinkedCount = useMemo(() => {
    let optimisticExtra = 0;
    pendingLinkedResumeIds.forEach(id => {
      if (!linkedResumeIds.has(id)) optimisticExtra += 1;
    });
    return eligibleLinkedResumes.length + optimisticExtra;
  }, [eligibleLinkedResumes.length, linkedResumeIds, pendingLinkedResumeIds]);

  useEffect(() => {
    const rerank = (items: MatchRanking[]) => (
      [...items]
        .sort((a, b) => b.score - a.score)
        .map((item, index) => ({ ...item, rank: index + 1 }))
    );

    setCandidates(prev => {
      if (prev.length === 0) return prev;
      const kept = prev.filter(item => item.resumeId && linkedResumeIds.has(item.resumeId));
      if (kept.length === prev.length) return prev;
      return rerank(kept);
    });

    setCandidatesFiltered(prev => {
      if (prev.length === 0) return prev;
      const kept = prev.filter(item => item.resumeId && linkedResumeIds.has(item.resumeId));
      if (kept.length === prev.length) return prev;
      return rerank(kept);
    });
  }, [linkedResumeIds]);

  useEffect(() => {
    if (!candidatesCacheKey) return;
    try {
      sessionStorage.setItem(candidatesCacheKey, JSON.stringify({ rankings: candidates, filtered: candidatesFiltered }));
    } catch { /* quota */ }
  }, [candidates, candidatesFiltered, candidatesCacheKey]);

  // Fetch JD detail
  useEffect(() => {
    if (!jobId) return;
    setLoading(true);
    (async () => {
      try {
        const res = await authFetch(`/api/job-descriptions/${jobId}`);
        if (res.ok) {
          const data = await res.json();
          setJd({
            id: jobId,
            title: data.title || 'Untitled',
            text: data.text || '',
            source: data.source || 'text',
            createdAt: data.createdAt || '',
            indexed: data.embeddingExists ?? (data.status === 'indexed' || !!data.embedding),
            tags: data.tags || [],
            dimensionWeights: data.dimensionWeights || {},
            dimensionWeightsReasoning: data.dimensionWeightsReasoning || {},
            dimensionWeightsReasoningDetails: data.dimensionWeightsReasoningDetails || {},
            weightsSource: data.weightsSource || '',
            jobStatus: data.jobStatus || 'open',
            closedAt: data.closedAt || '',
            closedBy: data.closedBy || '',
            filledResumeIds: data.filledResumeIds || [],
            filledCandidates: data.filledCandidates || [],
            uploadedBy: data.uploadedBy || '',
          });
        } else {
          setError('Job not found');
        }
      } catch {
        setError('Failed to load job');
      } finally {
        setLoading(false);
      }
    })();
  }, [jobId, authFetch]);

  // Match candidates (role scope — resumes linked to this JD)
  const runCandidateMatch = useCallback(async (opts?: { silent?: boolean }) => {
    if (!jobId || !jd?.indexed) return false;
    const silent = !!opts?.silent;
    // In-flight guard — prevent duplicate concurrent match requests
    if (matchInFlightRef.current) return false;
    matchInFlightRef.current = true;
    if (!silent) setCandidatesLoading(true);
    setError(null);
    try {
      const res = await authFetch('/api/match', {
        method: 'POST',
        body: JSON.stringify({ jd_id: jobId, scope: 'role', top_n: Math.min(eligibleLinkedResumes.length, 50) }),
      });
      const data = await res.json();

      // Cache hit — backend returns 200 with full results synchronously
      if (res.status === 200 && data.rankings) {
        const rankings = data.rankings ?? [];
        const filtered = data.filtered ?? [];
        setCandidates(rankings);
        setCandidatesFiltered(filtered);
        setDirtyReasons(new Set());
        if (!silent) setCandidatesLoading(false);
        setCandidatesRan(true);
        matchInFlightRef.current = false;
        try { sessionStorage.setItem(candidatesCacheKey, JSON.stringify({ rankings, filtered })); } catch { /* quota */ }
        return true;
      }

      // Async job — backend returns 202 with matchId, results arrive via Firestore
      if (res.status === 202 && data.matchId) {
        const unsub = onSnapshot(doc(db, 'matchJobs', data.matchId), (snap) => {
          const job = snap.data();
          if (!job) return;
          if (job.status === 'completed') {
            unsub();
            const rankings = job.rankings ?? [];
            const filtered = job.filtered ?? [];
            setCandidates(rankings);
            setCandidatesFiltered(filtered);
            setDirtyReasons(new Set());
            if (!silent) setCandidatesLoading(false);
            setCandidatesRan(true);
            matchInFlightRef.current = false;
            try { sessionStorage.setItem(candidatesCacheKey, JSON.stringify({ rankings, filtered })); } catch { /* quota */ }
          } else if (job.status === 'failed') {
            unsub();
            setError(job.error || 'Match failed');
            if (!silent) setCandidatesLoading(false);
            setCandidatesRan(true);
            matchInFlightRef.current = false;
          }
        });
        return true;
      }

      // Unexpected response
      setError(data?.detail?.message || data?.detail || 'Match failed');
      if (!silent) setCandidatesLoading(false);
      setCandidatesRan(true);
      matchInFlightRef.current = false;
      return false;
    } catch (e) {
      if (e instanceof QuotaExceededError) setError(e.message);
      else setError('Failed to match candidates');
      if (!silent) setCandidatesLoading(false);
      setCandidatesRan(true);
      matchInFlightRef.current = false;
      return false;
    }
  }, [jobId, jd?.indexed, authFetch, candidatesCacheKey, eligibleLinkedResumes.length]);

  useEffect(() => {
    if (tab !== 'candidates') return;
    if (!jd?.indexed) return;
    if (dirtyTick === lastAutoRematchTickRef.current) return;

    const needsRescore = Array.from(dirtyReasons).some(r => RESCORE_REASONS.has(r));
    if (!needsRescore) {
      // Non-scoring changes (unlink, tag changes) — just clear dirty, no API call
      if (dirtyReasons.size > 0) {
        setDirtyReasons(new Set());
        lastAutoRematchTickRef.current = dirtyTick;
      }
      return;
    }

    if (eligibleLinkedResumes.length === 0) {
      setDirtyReasons(new Set());
      lastAutoRematchTickRef.current = dirtyTick;
      return;
    }

    lastAutoRematchTickRef.current = dirtyTick;
    const timer = window.setTimeout(() => {
      // If we already have candidate rows, refresh in background without blocking UI.
      runCandidateMatch({ silent: candidates.length > 0 || candidatesRan });
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [tab, jd?.indexed, dirtyReasons, dirtyTick, eligibleLinkedResumes.length, runCandidateMatch, candidates.length, candidatesRan]);

  // If linked resumes are missing inline scores, request a one-shot backend backfill.
  // This pulls from pointwise cache first and fills Firestore inlineScores for stable UI.
  useEffect(() => {
    if (tab !== 'candidates') return;
    if (!jobId || !jd?.indexed) return;
    if (missingInlineScoreIds.length === 0) {
      scoreBackfillKeyRef.current = '';
      return;
    }

    const key = `${jobId}:${missingInlineScoreIds.join(',')}`;
    if (scoreBackfillKeyRef.current === key) return;
    scoreBackfillKeyRef.current = key;

    authFetch(`/api/job-descriptions/${jobId}/backfill-inline-scores`, {
      method: 'POST',
    }).catch(() => {
      // allow retry on next state transition
      scoreBackfillKeyRef.current = '';
    });
  }, [tab, jobId, jd?.indexed, missingInlineScoreIds, authFetch]);

  // If rows are scored but missing detailed analysis fields, hydrate from match cache silently.
  // This restores strengths/improvements and dimension explanations without UX flicker.
  useEffect(() => {
    if (tab !== 'candidates') return;
    if (!jobId || !jd?.indexed) return;
    if (missingDetailedAnalysisIds.length === 0) {
      detailHydrationKeyRef.current = '';
      return;
    }

    const key = `${jobId}:${missingDetailedAnalysisIds.join(',')}`;
    if (detailHydrationKeyRef.current === key) return;
    detailHydrationKeyRef.current = key;

    runCandidateMatch({ silent: true }).then((ok) => {
      if (!ok) {
        // allow retry on next meaningful state transition
        detailHydrationKeyRef.current = '';
      }
    }).catch(() => {
      detailHydrationKeyRef.current = '';
    });
  }, [tab, jobId, jd?.indexed, missingDetailedAnalysisIds, runCandidateMatch]);

  // No auto-match on tab switch — inline scores from Firestore are the primary display.
  // Users can click "Full Analysis" / "Re-match" to get detailed strengths/improvements.

  // Discover: global search across entire talent pool
  const runDiscover = useCallback(async () => {
    if (!jobId || !jd?.indexed) return;
    if (discoverInFlightRef.current) return;
    const tagFilterEnabled = canUseTagFilter && discoverApplyTagFilter;
    const sessionKey = `xrank:job-discover:${jobId}:${discoverTopN}:${tagFilterEnabled ? 'tag-on' : 'tag-off'}`;

    if (tagFilterEnabled) {
      const cachedRaw = sessionStorage.getItem(sessionKey);
      if (cachedRaw) {
        try {
          const cached = JSON.parse(cachedRaw) as { rankings?: MatchRanking[] };
          setDiscoverResults(cached.rankings ?? []);
          setDiscoverLoading(false);
          setDiscoverRan(true);
          return;
        } catch {
          sessionStorage.removeItem(sessionKey);
        }
      }
    }

    discoverInFlightRef.current = true;
    setDiscoverLoading(true);
    setError(null);
    try {
      const res = await authFetch('/api/match', {
        method: 'POST',
        body: JSON.stringify({
          jd_id: jobId,
          scope: 'all',
          top_n: discoverTopN,
          apply_tag_filter: tagFilterEnabled,
        }),
      });
      const data = await res.json();

      // Cache hit — backend returns 200 with full results synchronously
      if (res.status === 200 && data.rankings) {
        setDiscoverResults(data.rankings ?? []);
        if (tagFilterEnabled) {
          sessionStorage.setItem(sessionKey, JSON.stringify({ rankings: data.rankings ?? [] }));
        }
        setDiscoverLoading(false);
        setDiscoverRan(true);
        discoverInFlightRef.current = false;
        return;
      }

      // Async job — backend returns 202 with matchId
      if (res.status === 202 && data.matchId) {
        const unsub = onSnapshot(doc(db, 'matchJobs', data.matchId), (snap) => {
          const job = snap.data();
          if (!job) return;
          if (job.status === 'completed') {
            unsub();
            setDiscoverResults(job.rankings ?? []);
            if (tagFilterEnabled) {
              sessionStorage.setItem(sessionKey, JSON.stringify({ rankings: job.rankings ?? [] }));
            }
            setDiscoverLoading(false);
            setDiscoverRan(true);
            discoverInFlightRef.current = false;
          } else if (job.status === 'failed') {
            unsub();
            setError(job.error || 'Discover failed');
            setDiscoverLoading(false);
            setDiscoverRan(true);
            discoverInFlightRef.current = false;
          }
        });
        return;
      }

      // Unexpected response
      setError(data?.detail?.message || data?.detail || 'Discover failed');
      setDiscoverLoading(false);
      setDiscoverRan(true);
      discoverInFlightRef.current = false;
    } catch (e) {
      if (e instanceof QuotaExceededError) setError(e.message);
      else setError('Failed to discover candidates');
      setDiscoverLoading(false);
      setDiscoverRan(true);
      discoverInFlightRef.current = false;
    }
  }, [jobId, jd?.indexed, authFetch, discoverTopN, canUseTagFilter, discoverApplyTagFilter]);

  if (loading) {
    return (
      <div className="v2-page">
        <div className="v2-empty"><span className="v2-spinner" /> Loading job...</div>
      </div>
    );
  }

  if (error && !jd) {
    return (
      <div className="v2-page">
        <div className="v2-empty">
          <h3>Error</h3>
          <p>{error}</p>
          <button className="v2-btn v2-btn-secondary" onClick={() => navigate('/jobs')}>
            <IconArrowLeft /> Back to Jobs
          </button>
        </div>
      </div>
    );
  }

  const saveJobTitle = async () => {
    if (!jobId || !jd) return;
    const nextTitle = titleDraft.trim();
    if (!nextTitle || nextTitle === jd.title) {
      setTitleEditMode(false);
      return;
    }
    setTitleSaving(true);
    try {
      const res = await authFetch(`/api/job-descriptions/${jobId}`, {
        method: 'PATCH',
        body: JSON.stringify({ title: nextTitle }),
      });
      if (res.ok) {
        setJd(prev => (prev ? { ...prev, title: nextTitle } : prev));
        setTitleEditMode(false);
      }
    } catch {
      // no-op; keep current title
    } finally {
      setTitleSaving(false);
    }
  };

  const isJobOpen = !jd || jd.jobStatus === 'open';
  const canEditJob = canEditItem(jd?.uploadedBy, user?.uid, role);
  const editLockedTitle = canEditJob
    ? undefined
    : 'Only the job owner or an admin can change this.';
  const adminCanReassign = isOrgAdmin(role);

  const handleReopenJob = async () => {
    if (!jobId || !jd) return;
    try {
      const res = await authFetch(`/api/job-descriptions/${jobId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'open' }),
      });
      if (res.ok) {
        setJd(prev => prev ? { ...prev, jobStatus: 'open', closedAt: '', closedBy: '', filledResumeIds: [], filledCandidates: [] } : prev);
      }
    } catch { /* ignore */ }
  };

  const handleCloneJob = async () => {
    if (!jobId) return;
    try {
      const res = await authFetch(`/api/job-descriptions/${jobId}/clone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.jdId) navigate(`/jobs/${data.jdId}`);
      }
    } catch { /* ignore */ }
  };

  return (
    <div className="v2-page">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
        <button className="v2-btn v2-btn-ghost" onClick={() => navigate('/jobs')}>
          <IconArrowLeft /> Jobs
        </button>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          {jd && (
            <span
              title={`Owner: ${ownerLabel(jd.uploadedBy, org?.members, user?.uid)}`}
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-muted)',
                maxWidth: 200,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              Owner: {ownerLabel(jd.uploadedBy, org?.members, user?.uid)}
            </span>
          )}
          {adminCanReassign && jd && (
            <button
              className="v2-btn v2-btn-ghost"
              style={{ fontSize: 'var(--font-size-xs)' }}
              onClick={() => setShowTransferOwnership(true)}
              title="Reassign ownership of this job to another member"
            >
              Transfer ownership
            </button>
          )}
        </div>
      </div>

      <div className="v2-page-header">
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-1)' }}>
          {titleEditMode ? (
            <>
              <input
                value={titleDraft}
                onChange={e => setTitleDraft(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') saveJobTitle();
                  if (e.key === 'Escape') setTitleEditMode(false);
                }}
                style={{
                  fontSize: 'var(--font-size-xl)',
                  fontWeight: 'var(--font-weight-bold)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '2px 8px',
                  background: 'var(--bg-secondary)',
                  minWidth: 260,
                }}
                autoFocus
              />
              <button className="v2-btn v2-btn-ghost" style={{ fontSize: 12, padding: '2px 8px' }} onClick={saveJobTitle} disabled={titleSaving || !canEditJob} title={canEditJob ? undefined : editLockedTitle}>Save</button>
              <button className="v2-btn v2-btn-ghost" style={{ fontSize: 12, padding: '2px 8px' }} onClick={() => setTitleEditMode(false)}>Cancel</button>
            </>
          ) : (
            <>
              <span>{jd?.title}</span>
              {isJobOpen && canEditJob && (
                <button
                  className="v2-btn v2-btn-ghost"
                  style={{ fontSize: 12, padding: '1px 8px', lineHeight: 1 }}
                  title="Rename job"
                  onClick={() => {
                    setTitleDraft(jd?.title || '');
                    setTitleEditMode(true);
                  }}
                >
                  ✎
                </button>
              )}
              {jd && jd.jobStatus !== 'open' && (
                <span className={`v2-badge ${jd.jobStatus === 'filled' ? 'v2-badge-primary' : 'v2-badge-muted'}`} style={{ fontSize: 11, marginLeft: 4 }}>
                  {jd.jobStatus === 'filled' ? 'Filled' : 'Closed'}
                </span>
              )}
            </>
          )}
        </h1>
      </div>

      {/* Status banner for filled/closed jobs */}
      {jd && jd.jobStatus !== 'open' && (
        <div style={{
          background: jd.jobStatus === 'filled' ? 'rgba(34,197,94,0.08)' : 'rgba(156,163,175,0.10)',
          border: `1px solid ${jd.jobStatus === 'filled' ? 'rgba(34,197,94,0.25)' : 'rgba(156,163,175,0.25)'}`,
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-3) var(--space-4)',
          marginBottom: 'var(--space-4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 'var(--space-2)',
        }}>
          <div style={{ fontSize: 'var(--font-size-sm)', color: jd.jobStatus === 'filled' ? 'var(--color-success, #22c55e)' : 'var(--text-muted)' }}>
            {jd.jobStatus === 'filled'
              ? `This role was filled${jd.closedAt ? ` on ${new Date(jd.closedAt).toLocaleDateString()}` : ''}`
              : `This role was closed${jd.closedAt ? ` on ${new Date(jd.closedAt).toLocaleDateString()}` : ''}`
            }
            {jd.jobStatus === 'filled' && jd.filledCandidates.length > 0 && (
              <span
                title={`Filled by: ${jd.filledCandidates.map(c => (
                  revealIdentity ? displayResumeName(c.filename) : fallbackCandidateAlias(c.resumeId)
                )).join(', ')}`}
                style={{
                  display: 'block',
                  marginTop: 2,
                  fontSize: 'var(--font-size-xs)',
                  opacity: 0.85,
                  maxWidth: 460,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                Filled by: {jd.filledCandidates.map(c => (
                  revealIdentity ? displayResumeName(c.filename) : fallbackCandidateAlias(c.resumeId)
                )).join(', ')}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button
              className="v2-btn v2-btn-ghost"
              style={{ fontSize: 'var(--font-size-xs)' }}
              onClick={handleReopenJob}
              disabled={!canEditJob}
              title={editLockedTitle}
            >
              Reopen
            </button>
            <button className="v2-btn v2-btn-ghost" style={{ fontSize: 'var(--font-size-xs)' }} onClick={handleCloneJob}>Clone to new role</button>
          </div>
        </div>
      )}

      {/* Error banner */}
      {error && jd && (
        <div style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 'var(--radius-md)', padding: 'var(--space-3) var(--space-4)', marginBottom: 'var(--space-4)', color: 'var(--color-error)', fontSize: 'var(--font-size-sm)' }}>
          {error}
        </div>
      )}

      {addCandidatesStatus && (
        <div style={{ background: 'rgba(37,99,235,0.08)', border: '1px solid rgba(37,99,235,0.2)', borderRadius: 'var(--radius-md)', padding: 'var(--space-3) var(--space-4)', marginBottom: 'var(--space-4)', color: 'var(--text-primary)', fontSize: 'var(--font-size-sm)', whiteSpace: 'pre-wrap' }}>
          {addCandidatesStatus}
        </div>
      )}

      {/* Tabs */}
      <div className="v2-tabs job-workspace-tabs">
        <div className="job-workspace-tabs__buttons">
          <button className={`v2-tab ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')}>Overview & Requirements</button>
          {isJobOpen && (
            <button className={`v2-tab ${tab === 'candidates' ? 'active' : ''}`} onClick={() => setTab('candidates')}>Linked Candidates ({displayLinkedCount})</button>
          )}
        </div>
      </div>

      {/* Tab Content */}
      <div className="v2-tab-content">
        {tab === 'overview' && (
          <OverviewTab jd={jd!} authFetch={authFetch} canEdit={canEditJob} editLockedTitle={editLockedTitle} onDeleted={() => navigate('/jobs')} onTagsChanged={(tags) => {
            setJd(prev => prev ? { ...prev, tags } : prev);
            markDirty('jd_tags_changed');
          }} onWeightsChanged={(weights, source, reasoning, reasoningDetails) => {
            setJd(prev => prev ? {
              ...prev,
              dimensionWeights: weights,
              weightsSource: source,
              dimensionWeightsReasoning: reasoning ?? prev.dimensionWeightsReasoning,
              dimensionWeightsReasoningDetails: reasoningDetails ?? prev.dimensionWeightsReasoningDetails,
            } : prev);
            markDirty('jd_weights_changed');
          }} onStatusChanged={(status, filledIds) => {
            setJd(prev => prev ? { ...prev, jobStatus: status, filledResumeIds: filledIds || prev.filledResumeIds } : prev);
          }} rankedCandidatesForFill={fillRankedCandidates} onReRank={runCandidateMatch} onOpenAddCandidates={() => openAddCandidates('upload')} />
        )}
        {tab === 'candidates' && (
          <CandidatesTab
            rankings={candidates}
            filtered={candidatesFiltered}
            loading={candidatesLoading}
            ran={candidatesRan}
            linkedResumes={linkedResumesForView}
            expanded={expanded}
            onExpand={setExpanded}
            onRunMatch={runCandidateMatch}
            jdIndexed={!!jd?.indexed}
            authFetch={authFetch}
            jdTitle={jd?.title || ''}
            jobId={jobId || ''}
            onOpenAddCandidates={(section) => openAddCandidates(section)}
            onCandidateLinkageChanged={() => markDirty('manual_unlink')}
            dimensionWeights={chartDimensionWeights}
            readOnly={!isJobOpen}
            revealIdentity={revealIdentity}
            canEdit={canEditJob}
            editLockedTitle={editLockedTitle}
          />
        )}
      </div>

      {isJobOpen && addCandidatesOpen && (
        <AddCandidatesModal
          section={addCandidatesSection}
          onSectionChange={setAddCandidatesSection}
          onClose={() => setAddCandidatesOpen(false)}
          results={discoverResults}
          loading={discoverLoading}
          ran={discoverRan}
          topN={discoverTopN}
          onTopNChange={setDiscoverTopN}
          onRun={runDiscover}
          applyTagFilter={discoverApplyTagFilter}
          onApplyTagFilterChange={setDiscoverApplyTagFilter}
          canUseTagFilter={canUseTagFilter}
          jdIndexed={!!jd?.indexed}
          authFetch={authFetch}
          jobId={jobId || ''}
          linkedResumeIds={linkedResumeIds}
          ineligibleResumeIds={ineligibleResumeIds}
          revealIdentity={revealIdentity}
          onLinked={(resumeId) => {
            setPendingLinkedResumeIds(prev => new Set(prev).add(resumeId));
            markDirty('discover_linked');
          }}
          onStatus={showAddCandidatesStatus}
          onViewLinked={() => {
            setTab('candidates');
            setAddCandidatesOpen(false);
          }}
        />
      )}

      {jd && jobId && (
        <TransferOwnershipModal
          open={showTransferOwnership}
          kind="job"
          itemId={jobId}
          itemLabel={jd.title || 'Untitled job'}
          currentOwnerUid={jd.uploadedBy}
          onClose={() => setShowTransferOwnership(false)}
          onTransferred={(newOwnerUid) => {
            setJd(prev => prev ? { ...prev, uploadedBy: newOwnerUid } : prev);
          }}
        />
      )}
    </div>
  );
};

/* ── Overview Tab ── */
function OverviewTab({ jd, authFetch, canEdit, editLockedTitle, onDeleted, onTagsChanged, onWeightsChanged, onStatusChanged, rankedCandidatesForFill, onReRank, onOpenAddCandidates }: {
  jd: JDDetail;
  authFetch: ReturnType<typeof useAuthFetch>;
  canEdit: boolean;
  editLockedTitle?: string;
  onDeleted: () => void;
  onTagsChanged: (tags: string[]) => void;
  onWeightsChanged: (
    weights: Record<string, number>,
    source: string,
    reasoning?: Record<string, string>,
    reasoningDetails?: JDDetail['dimensionWeightsReasoningDetails'],
  ) => void;
  onStatusChanged: (status: 'open' | 'filled' | 'closed', filledResumeIds?: string[]) => void;
  rankedCandidatesForFill: Array<{ resumeId: string; displayName: string; score: number | null }>;
  onReRank: () => Promise<boolean> | void;
  onOpenAddCandidates: () => void;
}) {
  const DIMS = useMemo(() => [...RADAR_DIMENSION_KEYS] as RadarDimensionKey[], []);
  const hasWeights = jd.dimensionWeights && Object.keys(jd.dimensionWeights).length > 0;
  const [expandedDim, setExpandedDim] = useState<string | null>(null);
  const toggleDimReasoning = (dim: string) => setExpandedDim(prev => prev === dim ? null : dim);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showFillPicker, setShowFillPicker] = useState(false);
  const [fillPickerLoading, setFillPickerLoading] = useState(false);
  const [fillPickerResumes, setFillPickerResumes] = useState<Array<{ resumeId: string; displayName: string; score: number | null }>>([]);
  const [selectedFillIds, setSelectedFillIds] = useState<Set<string>>(new Set());
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [tagSaving, setTagSaving] = useState(false);
  const [customTagInput, setCustomTagInput] = useState('');
  const [sliders, setSliders] = useState<Record<string, number>>({});
  const [lastEditedDim, setLastEditedDim] = useState<string | null>(null);
  const [weightSaving, setWeightSaving] = useState(false);
  const [aiRecalculating, setAiRecalculating] = useState(false);
  const { tagConflictModal, openTagConflictModal, closeTagConflictModal } = useTagConflictModal('Tag update failed due to validation rules.');
  const isOpen = jd.jobStatus === 'open';

  useEffect(() => {
    const pcts: Record<string, number> = {};
    if (hasWeights) {
      for (const k of DIMS) {
        pcts[k] = Math.round((jd.dimensionWeights[k] || 0.125) * 200) / 2;
      }
      const total = Object.values(pcts).reduce((s, v) => s + v, 0);
      if (total !== 100 && total > 0) {
        const maxK = Object.entries(pcts).sort((a, b) => b[1] - a[1])[0][0];
        pcts[maxK] = Math.round((pcts[maxK] + (100 - total)) * 2) / 2;
      }
    } else {
      DIMS.forEach(k => { pcts[k] = 12.5; });
    }
    setSliders(pcts);
  }, [DIMS, jd.dimensionWeights, hasWeights]);

  const normalize = (raw: Record<string, number>, anchorDim?: string | null) => {
    const keys = Object.keys(raw);
    if (keys.length === 0) return raw;

    const clampPct = (v: number) => Math.max(1, Math.min(50, v));
    const roundedRaw: Record<string, number> = {};
    keys.forEach((k) => {
      roundedRaw[k] = clampPct(Math.round((raw[k] || 0) * 2) / 2);
    });

    // Fallback to proportional normalization when no anchor is available.
    if (!anchorDim || !(anchorDim in roundedRaw)) {
      const t = Object.values(roundedRaw).reduce((s, v) => s + v, 0);
      if (t === 0) return roundedRaw;
      const out: Record<string, number> = {};
      keys.forEach((k) => { out[k] = Math.round((roundedRaw[k] / t) * 10000) / 10000; });
      const diff = Math.round((1 - Object.values(out).reduce((s, v) => s + v, 0)) * 10000) / 10000;
      if (diff !== 0) {
        const maxK = Object.entries(out).sort((a, b) => b[1] - a[1])[0][0];
        out[maxK] = Math.round((out[maxK] + diff) * 10000) / 10000;
      }
      return out;
    }

    const anchorPct = clampPct(roundedRaw[anchorDim]);
    const others = keys.filter((k) => k !== anchorDim);
    const targetOthersTotal = 100 - anchorPct;
    const rawOthersSum = others.reduce((s, k) => s + roundedRaw[k], 0);

    const othersPct: Record<string, number> = {};
    if (rawOthersSum > 0) {
      others.forEach((k) => {
        othersPct[k] = (roundedRaw[k] / rawOthersSum) * targetOthersTotal;
      });
    } else {
      const equal = targetOthersTotal / Math.max(others.length, 1);
      others.forEach((k) => { othersPct[k] = equal; });
    }

    const fixed = new Set<string>();
    let guard = 0;
    while (guard < 12) {
      guard += 1;
      let changed = false;

      others.forEach((k) => {
        if (fixed.has(k)) return;
        if (othersPct[k] < 1) {
          othersPct[k] = 1;
          fixed.add(k);
          changed = true;
        } else if (othersPct[k] > 50) {
          othersPct[k] = 50;
          fixed.add(k);
          changed = true;
        }
      });

      if (!changed) break;

      const free = others.filter((k) => !fixed.has(k));
      if (free.length === 0) break;

      const fixedSum = others
        .filter((k) => fixed.has(k))
        .reduce((s, k) => s + othersPct[k], 0);
      const remaining = targetOthersTotal - fixedSum;
      const freeRawSum = free.reduce((s, k) => s + roundedRaw[k], 0);

      if (freeRawSum > 0) {
        free.forEach((k) => {
          othersPct[k] = (roundedRaw[k] / freeRawSum) * remaining;
        });
      } else {
        const equal = remaining / free.length;
        free.forEach((k) => { othersPct[k] = equal; });
      }
    }

    const out: Record<string, number> = {};
    out[anchorDim] = Math.round((anchorPct / 100) * 10000) / 10000;
    others.forEach((k) => {
      out[k] = Math.round(((othersPct[k] || 0) / 100) * 10000) / 10000;
    });

    const sumOut = Object.values(out).reduce((s, v) => s + v, 0);
    const diff = Math.round((1 - sumOut) * 10000) / 10000;
    if (diff !== 0) {
      const adjustKey = others
        .slice()
        .sort((a, b) => (out[b] || 0) - (out[a] || 0))[0] || anchorDim;
      out[adjustKey] = Math.round(((out[adjustKey] || 0) + diff) * 10000) / 10000;
    }
    return out;
  };

  const handleSliderChange = (dim: string, val: number) => {
    setLastEditedDim(dim);
    setSliders(prev => ({ ...prev, [dim]: Math.max(1, Math.min(50, Math.round(val * 2) / 2)) }));
  };

  const sliderTotal = useMemo(
    () => Math.round(Object.values(sliders).reduce((s, v) => s + v, 0) * 10) / 10,
    [sliders],
  );

  const isWeightDirty = hasWeights && DIMS.some(k => {
    const orig = Math.round((jd.dimensionWeights[k] || 0.125) * 200) / 2;
    return orig !== (sliders[k] || 0);
  });

  const radarSeries = useMemo(() => {
    const values = DIMS.map(dim => sliders[dim] || 0);
    const max = Math.max(...values, 0.001);
    return values.map(v => (v / max) * 100);
  }, [DIMS, sliders]);

  const radarPoints = useMemo(() => {
    const centerX = 150;
    const centerY = 150;
    const radius = 94;
    return radarSeries.map((value, index) => {
      const angle = ((Math.PI * 2) / radarSeries.length) * index - Math.PI / 2;
      const pointRadius = (value / 100) * radius;
      const x = centerX + pointRadius * Math.cos(angle);
      const y = centerY + pointRadius * Math.sin(angle);
      return `${x},${y}`;
    }).join(' ');
  }, [radarSeries]);

  const dominantDimension = useMemo(() => {
    const top = DIMS
      .map(dim => ({ dim, value: sliders[dim] || 0 }))
      .sort((a, b) => b.value - a.value)[0];
    return top ? DIMENSION_META[top.dim].label : 'N/A';
  }, [DIMS, sliders]);

  const [coreDimensions, domainDimensions] = useMemo(() => {
    const mid = Math.ceil(DIMS.length / 2);
    return [DIMS.slice(0, mid), DIMS.slice(mid)];
  }, [DIMS]);

  const saveTags = async (newTags: string[]) => {
    setTagSaving(true);
    try {
      const res = await authFetch(`/api/job-descriptions/${jd.id}/tags`, {
        method: 'PUT',
        body: JSON.stringify({ tags: newTags }),
      });
      if (res.ok) {
        onTagsChanged(newTags);
        return true;
      }

      const data = await res.json().catch(() => null);
      const backendConflicts = extractTagErrors(data);
      openTagConflictModal(newTags[newTags.length - 1] || '', backendConflicts, 'Tag update failed due to validation rules.');
      return false;
    } catch {
      openTagConflictModal(newTags[newTags.length - 1] || '', ['Could not update tags right now. Please retry.']);
      return false;
    } finally {
      setTagSaving(false);
    }
  };

  const removeTag = async (tag: string) => {
    if (tagSaving) return;
    await saveTags(jd.tags.filter(t => t !== tag));
  };

  const addTag = async () => {
    const tag = customTagInput.trim();
    if (!tag) return;
    if (jd.tags.some(existing => existing.toLowerCase() === tag.toLowerCase())) {
      setCustomTagInput('');
      return;
    }

    const preview = await previewTagAddition(authFetch, 'job', jd.tags, tag);
    if (!preview.allowed) {
      openTagConflictModal(tag, [...preview.errors, ...formatConflictMessages(preview.conflicts)]);
      return;
    }

    const saved = await saveTags([...jd.tags, tag]);
    if (saved) {
      setCustomTagInput('');
    }
  };

  const saveWeights = async () => {
    setWeightSaving(true);
    try {
      const normalized = normalize(sliders, lastEditedDim);
      const res = await authFetch(`/api/job-descriptions/${jd.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ dimensionWeights: normalized }),
      });
      if (res.ok) {
        onWeightsChanged(normalized, 'manual');
        await Promise.resolve(onReRank());
      }
    } catch {
      // no-op
    } finally {
      setWeightSaving(false);
    }
  };

  const recalculateWeightsWithAi = async () => {
    setAiRecalculating(true);
    try {
      const res = await authFetch(`/api/job-descriptions/${jd.id}/recalculate-weights`, {
        method: 'POST',
      });
      if (!res.ok) return;
      const data = await res.json();
      const nextWeights = data.dimensionWeights || {};
      const nextReasoning = data.dimensionWeightsReasoning || {};
      const nextReasoningDetails = data.dimensionWeightsReasoningDetails || {};
      const source = data.weightsSource || 'auto';
      onWeightsChanged(nextWeights, source, nextReasoning, nextReasoningDetails);
      await Promise.resolve(onReRank());
    } catch {
      // no-op
    } finally {
      setAiRecalculating(false);
    }
  };

  const handleArchiveJob = async () => {
    setUpdatingStatus(true);
    try {
      const res = await authFetch(`/api/job-descriptions/${jd.id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'closed' }),
      });
      if (res.ok) {
        setShowCloseModal(false);
        onStatusChanged('closed');
        onDeleted();
      }
    } catch {
      // no-op
    } finally {
      setUpdatingStatus(false);
    }
  };

  const openFillPicker = () => {
    setSelectedFillIds(new Set());
    setFillPickerLoading(false);
    setFillPickerResumes(rankedCandidatesForFill);
    setShowFillPicker(true);
  };

  const handleFillConfirm = async () => {
    setUpdatingStatus(true);
    try {
      const filledIds = Array.from(selectedFillIds);
      const res = await authFetch(`/api/job-descriptions/${jd.id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'filled', filledResumeIds: filledIds }),
      });
      if (res.ok) {
        setShowFillPicker(false);
        onStatusChanged('filled', filledIds);
        onDeleted();
      }
    } catch {
      // no-op
    } finally {
      setUpdatingStatus(false);
    }
  };

  const toggleFillCandidate = (resumeId: string) => {
    setSelectedFillIds(prev => {
      const next = new Set(prev);
      if (next.has(resumeId)) next.delete(resumeId);
      else next.add(resumeId);
      return next;
    });
  };

  return (
    <div className="job-overview-layout">
      <article className="job-overview-pane job-overview-pane--description">
        <div className="job-overview-pane-header">
          <span>Position Details</span>
          <h2>Full Job Description</h2>
        </div>

        <div className="job-overview-tags-row">
          {jd.tags.map(tag => (
            <span key={tag} className="job-overview-tag job-overview-tag--static">
              <span className="job-overview-tag__label">{tag}</span>
              <button
                type="button"
                className="job-overview-tag__remove"
                onClick={() => removeTag(tag)}
                aria-label={`Remove ${tag} tag`}
                disabled={tagSaving || !canEdit}
                title={canEdit ? undefined : editLockedTitle}
              >
                x
              </button>
            </span>
          ))}
          <div className="job-overview-tag-input-wrap">
            <input
              type="text"
              className="job-overview-tag-input"
              placeholder={canEdit ? '+ Custom tag' : 'Tag editing locked'}
              value={customTagInput}
              onChange={e => setCustomTagInput(e.target.value)}
              maxLength={MAX_TAG_LENGTH}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addTag();
                }
              }}
              disabled={tagSaving || !canEdit}
              title={canEdit ? undefined : editLockedTitle}
            />
            <button
              type="button"
              className="job-overview-tag-add-btn"
              onClick={addTag}
              disabled={tagSaving || !customTagInput.trim() || !canEdit}
              title={canEdit ? undefined : editLockedTitle}
            >
              Add
            </button>
          </div>
          {tagSaving && <span className="v2-spinner" aria-hidden="true" />}
        </div>

        <TagConflictAlert
          show={tagConflictModal.show}
          pendingTag={tagConflictModal.pendingTag}
          conflicts={tagConflictModal.conflicts}
          onClose={closeTagConflictModal}
        />

        <div className="job-overview-description-scroll">
          <pre>{jd.text || 'No job description available.'}</pre>
        </div>
      </article>

      <section className="job-overview-right">
        <div className="job-overview-right-top">
          <article className="job-overview-pane job-overview-pane--radar">
            <div className="job-overview-pane-header">
              <span>Requirement Architecture</span>
              <h2>8D Diagnostic Signature</h2>
            </div>

            <div className="job-overview-radar-wrap">
              <svg viewBox="0 0 300 300" role="img" aria-label="Job dimension signature radar">
                {[40, 70, 100].map((pct) => (
                  <circle key={pct} cx="150" cy="150" r={pct * 0.94} fill="none" stroke="rgba(148,163,184,0.18)" strokeWidth="1" />
                ))}
                {DIMS.map((_, index) => {
                  const angle = ((Math.PI * 2) / DIMS.length) * index - Math.PI / 2;
                  const x = 150 + 94 * Math.cos(angle);
                  const y = 150 + 94 * Math.sin(angle);
                  return <line key={index} x1="150" y1="150" x2={x} y2={y} stroke="rgba(148,163,184,0.2)" strokeWidth="1" />;
                })}
                <polygon points={radarPoints} fill="rgba(14, 165, 233, 0.22)" stroke="rgba(2, 132, 199, 0.95)" strokeWidth="2" />
                {DIMS.map((dim, index) => {
                  const angle = ((Math.PI * 2) / DIMS.length) * index - Math.PI / 2;
                  const x = 150 + 114 * Math.cos(angle);
                  const y = 150 + 114 * Math.sin(angle);
                  return (
                    <text
                      key={`${dim}-label`}
                      x={x}
                      y={y}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize="10"
                      fill="var(--text-muted)"
                    >
                      {getRadarDimensionLabel(dim, 'short')}
                    </text>
                  );
                })}
              </svg>
            </div>

            <p className="job-overview-radar-note">
              Diagnostic: strongest emphasis on {dominantDimension}.
            </p>

          </article>

          <aside className="job-overview-pane job-overview-pane--actions">
            <div className="job-overview-pane-header">
              <span>Management</span>
              <h2>Job Actions</h2>
            </div>

            {isOpen ? (
              <div className="job-overview-action-stack">
                <button
                  className="v2-btn v2-btn-primary"
                  onClick={onOpenAddCandidates}
                  disabled={updatingStatus || !canEdit}
                  title={canEdit ? undefined : editLockedTitle}
                >
                  Add Candidates
                </button>
                <button
                  className="v2-btn v2-btn-primary"
                  onClick={openFillPicker}
                  disabled={updatingStatus || !canEdit}
                  title={canEdit ? undefined : editLockedTitle}
                >
                  Fill Job Now
                </button>
                <button
                  className="v2-btn v2-btn-ghost"
                  onClick={() => setShowCloseModal(true)}
                  disabled={updatingStatus || !canEdit}
                  title={canEdit ? undefined : editLockedTitle}
                >
                  Close / Archive
                </button>
              </div>
            ) : (
              <div className="job-overview-archived">Role is currently {jd.jobStatus === 'filled' ? 'filled' : 'closed'}.</div>
            )}

            <div className="job-overview-meta-list">
              <div><span>Current Status</span><strong>{jd.jobStatus === 'open' ? 'Active' : jd.jobStatus === 'filled' ? 'Filled' : 'Closed'}</strong></div>
              <div><span>Linked Candidates</span><strong>{rankedCandidatesForFill.length}</strong></div>
              <div><span>Weights Source</span><strong>{jd.weightsSource === 'manual' ? 'Custom' : 'AI'}</strong></div>
            </div>
          </aside>
        </div>

        <section className="job-overview-pane job-overview-pane--weights">
          <div className="job-overview-pane-header">
            <span>Search Parameters</span>
            <h2>Dimension Weighting</h2>
            <a
              href="#/settings/dimension-standards"
              style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-primary)', marginTop: 4, textDecoration: 'underline' }}
            >
              Read more about dimensions and standards
            </a>
          </div>

          <div className="job-overview-weight-grid">
            <div className="job-overview-weight-group">
              <h3>Core Dimensions</h3>
              <div className="job-overview-sliders">
                {coreDimensions.map(dim => {
                  const reasoning = jd.dimensionWeightsReasoning?.[dim];
                  const reasoningDetails = jd.dimensionWeightsReasoningDetails?.[dim];
                  const hasReasoning = !!(reasoning || reasoningDetails);
                  const isExpanded = expandedDim === dim;
                  const origPct = hasWeights
                    ? Math.round((jd.dimensionWeights[dim] || 0.125) * 200) / 2
                    : 12.5;
                  const isOverridden = (sliders[dim] || 0) !== origPct;
                  return (
                    <div key={dim} className="job-overview-slider-row">
                      <div
                        className="job-overview-slider-row__head"
                        style={{ cursor: hasReasoning ? 'pointer' : 'default' }}
                        onClick={() => hasReasoning && toggleDimReasoning(dim)}
                        title={hasReasoning ? (isExpanded ? 'Click to collapse' : 'Click to see AI reasoning') : undefined}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          {DIMENSION_META[dim].label}
                          {hasReasoning && (
                            <span style={{ fontSize: 11, color: 'var(--color-primary, #3b82f6)', lineHeight: 1, userSelect: 'none' }}>
                              {isExpanded ? '▲' : '▼'}
                            </span>
                          )}
                        </span>
                        <strong>{sliders[dim] || 0}%</strong>
                      </div>
                      {isExpanded && hasReasoning && (
                        <div style={{
                          fontSize: 12,
                          background: 'var(--bg-secondary, #f9fafb)',
                          border: '1px solid var(--border-light, #e5e7eb)',
                          borderRadius: 6,
                          padding: '8px 10px',
                          marginBottom: 4,
                          lineHeight: 1.5,
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                            <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>AI Reasoning</span>
                            {isOverridden && (
                              <span style={{ fontSize: 10, color: 'var(--color-warning, #d97706)', fontWeight: 500 }}>⚠ You've overridden this value</span>
                            )}
                          </div>
                          <span style={{ color: 'var(--text-muted)' }}>{reasoningDetails?.rationale || reasoning}</span>
                          {reasoningDetails?.jdSignals && reasoningDetails.jdSignals.length > 0 && (
                            <div style={{ marginTop: 6, color: 'var(--text-muted)' }}>
                              <strong>JD signals:</strong> {reasoningDetails.jdSignals.join('; ')}
                            </div>
                          )}
                          {(reasoningDetails?.standards?.onet || reasoningDetails?.standards?.shrm) && (
                            <div style={{ marginTop: 6, color: 'var(--text-muted)' }}>
                              <strong>Standards:</strong> {reasoningDetails?.standards?.onet || 'Not specified'} / {reasoningDetails?.standards?.shrm || 'Not specified'}
                            </div>
                          )}
                          {typeof reasoningDetails?.confidence === 'number' && (
                            <div style={{ marginTop: 6, color: 'var(--text-muted)' }}>
                              <strong>Confidence:</strong> {Math.round(reasoningDetails.confidence * 100)}%
                            </div>
                          )}
                          {reasoningDetails?.riskIfOverweighted && (
                            <div style={{ marginTop: 6, color: 'var(--text-muted)' }}>
                              <strong>Risk if overweighted:</strong> {reasoningDetails.riskIfOverweighted}
                            </div>
                          )}
                        </div>
                      )}
                      <input
                        type="range"
                        min={1}
                        max={50}
                        step={0.5}
                        value={sliders[dim] || 0}
                        onChange={e => handleSliderChange(dim, parseFloat(e.target.value))}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="job-overview-weight-group">
              <h3>Domain Weighting</h3>
              <div className="job-overview-sliders">
                {domainDimensions.map(dim => {
                  const reasoning = jd.dimensionWeightsReasoning?.[dim];
                  const reasoningDetails = jd.dimensionWeightsReasoningDetails?.[dim];
                  const hasReasoning = !!(reasoning || reasoningDetails);
                  const isExpanded = expandedDim === dim;
                  const origPct = hasWeights
                    ? Math.round((jd.dimensionWeights[dim] || 0.125) * 200) / 2
                    : 12.5;
                  const isOverridden = (sliders[dim] || 0) !== origPct;
                  return (
                    <div key={dim} className="job-overview-slider-row">
                      <div
                        className="job-overview-slider-row__head"
                        style={{ cursor: hasReasoning ? 'pointer' : 'default' }}
                        onClick={() => hasReasoning && toggleDimReasoning(dim)}
                        title={hasReasoning ? (isExpanded ? 'Click to collapse' : 'Click to see AI reasoning') : undefined}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          {DIMENSION_META[dim].label}
                          {hasReasoning && (
                            <span style={{ fontSize: 11, color: 'var(--color-primary, #3b82f6)', lineHeight: 1, userSelect: 'none' }}>
                              {isExpanded ? '▲' : '▼'}
                            </span>
                          )}
                        </span>
                        <strong>{sliders[dim] || 0}%</strong>
                      </div>
                      {isExpanded && hasReasoning && (
                        <div style={{
                          fontSize: 12,
                          background: 'var(--bg-secondary, #f9fafb)',
                          border: '1px solid var(--border-light, #e5e7eb)',
                          borderRadius: 6,
                          padding: '8px 10px',
                          marginBottom: 4,
                          lineHeight: 1.5,
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                            <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>AI Reasoning</span>
                            {isOverridden && (
                              <span style={{ fontSize: 10, color: 'var(--color-warning, #d97706)', fontWeight: 500 }}>⚠ You've overridden this value</span>
                            )}
                          </div>
                          <span style={{ color: 'var(--text-muted)' }}>{reasoningDetails?.rationale || reasoning}</span>
                          {reasoningDetails?.jdSignals && reasoningDetails.jdSignals.length > 0 && (
                            <div style={{ marginTop: 6, color: 'var(--text-muted)' }}>
                              <strong>JD signals:</strong> {reasoningDetails.jdSignals.join('; ')}
                            </div>
                          )}
                          {(reasoningDetails?.standards?.onet || reasoningDetails?.standards?.shrm) && (
                            <div style={{ marginTop: 6, color: 'var(--text-muted)' }}>
                              <strong>Standards:</strong> {reasoningDetails?.standards?.onet || 'Not specified'} / {reasoningDetails?.standards?.shrm || 'Not specified'}
                            </div>
                          )}
                          {typeof reasoningDetails?.confidence === 'number' && (
                            <div style={{ marginTop: 6, color: 'var(--text-muted)' }}>
                              <strong>Confidence:</strong> {Math.round(reasoningDetails.confidence * 100)}%
                            </div>
                          )}
                          {reasoningDetails?.riskIfOverweighted && (
                            <div style={{ marginTop: 6, color: 'var(--text-muted)' }}>
                              <strong>Risk if overweighted:</strong> {reasoningDetails.riskIfOverweighted}
                            </div>
                          )}
                        </div>
                      )}
                      <input
                        type="range"
                        min={1}
                        max={50}
                        step={0.5}
                        value={sliders[dim] || 0}
                        onChange={e => handleSliderChange(dim, parseFloat(e.target.value))}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="job-overview-rematch-row">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 13,
                fontWeight: 500,
                color: sliderTotal === 100
                  ? 'var(--color-success, #059669)'
                  : Math.abs(sliderTotal - 100) <= 15
                    ? 'var(--color-warning, #d97706)'
                    : 'var(--color-danger, #dc2626)',
              }}>
                <span>{sliderTotal === 100 ? '✓' : '⚠'}</span>
                <span>Total: {sliderTotal}%</span>
                {sliderTotal !== 100 && (
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>
                    — will be normalized to 100% on save, relative priorities preserved
                  </span>
                )}
              </div>
              {sliderTotal !== 100 && lastEditedDim && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  Last edited: {DIMENSION_META[lastEditedDim as keyof typeof DIMENSION_META]?.label || lastEditedDim}. This value will be preserved while others are rebalanced.
                </div>
              )}
              <button className="v2-btn v2-btn-primary" onClick={saveWeights} disabled={weightSaving || !isWeightDirty || !canEdit} title={canEdit ? undefined : editLockedTitle}>
                {weightSaving ? <><span className="v2-spinner" style={{ width: 14, height: 14 }} /> Saving...</> : 'Save & Re-match'}
              </button>
              {jd.weightsSource === 'manual' && (
                <button
                  className="v2-btn v2-btn-ghost"
                  onClick={recalculateWeightsWithAi}
                  disabled={aiRecalculating || !canEdit}
                  title={canEdit ? 'Recalculate original AI distribution' : editLockedTitle}
                >
                  {aiRecalculating ? <><span className="v2-spinner" style={{ width: 14, height: 14 }} /> Recalculating...</> : 'Recalculate with AI'}
                </button>
              )}
            </div>
          </div>
        </section>
      </section>

      {showCloseModal && (
        <div className="v2-modal-overlay" onClick={() => setShowCloseModal(false)}>
          <div className="v2-modal" onClick={e => e.stopPropagation()}>
            <div className="v2-modal-header">
              <h3>Close / Archive Job</h3>
            </div>
            <div className="v2-modal-body">
              <p style={{ marginBottom: 'var(--space-4)' }}>
                Archive <strong>{jd.title}</strong>? You can reopen this job later from history.
              </p>
            </div>
            <div className="v2-modal-footer">
              <button className="v2-btn v2-btn-ghost" onClick={() => setShowCloseModal(false)}>Cancel</button>
              <button className="v2-btn v2-btn-primary" onClick={handleArchiveJob} disabled={updatingStatus}>
                {updatingStatus ? <><span className="v2-spinner" style={{ width: 14, height: 14 }} /> Saving...</> : 'Close / Archive'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showFillPicker && (
        <div className="v2-modal-overlay" onClick={() => setShowFillPicker(false)}>
          <div className="v2-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="v2-modal-header">
              <h3>Who was hired?</h3>
            </div>
            <div className="v2-modal-body">
              <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)', marginBottom: 'var(--space-3)', marginTop: 0 }}>
                Select the candidate(s) who filled this role. Candidates are sorted by match score.
              </p>
              {fillPickerLoading ? (
                <div style={{ textAlign: 'center', padding: 'var(--space-4)' }}><span className="v2-spinner" /></div>
              ) : fillPickerResumes.length === 0 ? (
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
                  No candidates linked to this role. You can still mark it as filled.
                </p>
              ) : (
                <div style={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {fillPickerResumes.map((r, index) => {
                    const checked = selectedFillIds.has(r.resumeId);
                    return (
                      <label
                        key={r.resumeId}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
                          padding: 'var(--space-2) var(--space-3)',
                          borderRadius: 'var(--radius-sm)',
                          cursor: 'pointer',
                          background: checked ? 'rgba(34,197,94,0.06)' : 'transparent',
                          border: checked ? '1px solid rgba(34,197,94,0.3)' : '1px solid transparent',
                          transition: 'background 0.15s, border-color 0.15s',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleFillCandidate(r.resumeId)}
                          style={{ accentColor: 'var(--color-success, #22c55e)', width: 16, height: 16 }}
                        />
                        <span style={{ fontSize: 'var(--font-size-xs)', minWidth: 34, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                          #{index + 1}
                        </span>
                        <span
                          title={r.displayName}
                          style={{
                            fontSize: 'var(--font-size-sm)',
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {r.displayName}
                        </span>
                        <span
                          className={`v2-score ${scoreClass(r.score ?? 0)}`}
                          style={{ fontSize: 11, minWidth: 44, flexShrink: 0, textAlign: 'center', opacity: r.score == null ? 0.55 : 1 }}
                        >
                          {r.score == null ? '—' : `${Math.round(r.score)}%`}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="v2-modal-footer" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button className="v2-btn v2-btn-ghost" onClick={() => setShowFillPicker(false)}>Cancel</button>
              <button
                className="v2-btn v2-btn-primary"
                style={{ background: 'var(--color-success, #22c55e)' }}
                disabled={updatingStatus}
                onClick={handleFillConfirm}
              >
                {updatingStatus ? <><span className="v2-spinner" style={{ width: 14, height: 14 }} /> Saving...</> : `Mark as Filled${selectedFillIds.size > 0 ? ` (${selectedFillIds.size})` : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

/* ── Candidates Tab ── */
function CandidatesTab({
  rankings, filtered, loading, ran, linkedResumes, expanded, onExpand, onRunMatch, jdIndexed, authFetch, jdTitle, jobId, onOpenAddCandidates, onCandidateLinkageChanged, dimensionWeights, readOnly, revealIdentity, canEdit, editLockedTitle,
}: {
  rankings: MatchRanking[];
  filtered: MatchRanking[];
  loading: boolean;
  ran: boolean;
  linkedResumes: { resumeId: string; filename: string; status: string; inlineScores?: Record<string, { score: number; summary: string; scoredAt: string; dimensionScores?: Record<string, number>; dimensionExplanations?: Record<string, string> }> }[];
  expanded: string | null;
  onExpand: (f: string | null) => void;
  onRunMatch: () => void;
  jdIndexed: boolean;
  authFetch: ReturnType<typeof useAuthFetch>;
  jdTitle: string;
  jobId: string;
  onOpenAddCandidates: (section: 'upload' | 'talent') => void;
  onCandidateLinkageChanged: () => void;
  dimensionWeights: Record<string, number>;
  readOnly?: boolean;
  revealIdentity: boolean;
  canEdit: boolean;
  editLockedTitle?: string;
}) {
  const [selectedCandidateKey, setSelectedCandidateKey] = useState<string | null>(null);
  const [unlinking, setUnlinking] = useState<string | null>(null);
  const linkedResumeIds = useMemo(() => new Set(linkedResumes.map(r => r.resumeId)), [linkedResumes]);
  const chartsRef = useRef<HTMLDivElement>(null);

  const rankingByResumeId = useMemo(() => {
    const map = new Map<string, MatchRanking>();
    rankings
      .filter(r => r.resumeId && linkedResumeIds.has(r.resumeId))
      .forEach(r => {
        map.set(r.resumeId, {
          ...r,
          resumeFilename: displayCandidateName(r, revealIdentity),
          hasScore: true,
        });
      });
    return map;
  }, [rankings, linkedResumeIds, revealIdentity]);

  const linkedCandidateRows: MatchRanking[] = useMemo(() => {
    return linkedResumes.map((linked) => {
      const server = rankingByResumeId.get(linked.resumeId);
      const inlineScore = linked.inlineScores?.[jobId];

      const base: MatchRanking = server || (inlineScore ? {
        resumeId: linked.resumeId,
        resumeFilename: linked.filename,
        score: inlineScore.score,
        rank: 0,
        strengths: [],
        improvements: [],
        explanation: inlineScore.summary,
        dimensionScores: inlineScore.dimensionScores,
        dimensionExplanations: inlineScore.dimensionExplanations,
        hasScore: true,
      } : {
        resumeId: linked.resumeId,
        resumeFilename: linked.filename,
        score: 0,
        rank: 0,
        strengths: [],
        improvements: [],
        explanation: linked.status === 'indexed' ? 'Scored results are not available yet.' : 'This resume is still processing.',
        hasScore: false,
      });

      return {
        ...base,
        resumeId: linked.resumeId,
        resumeFilename: base.resumeFilename || linked.filename,
        candidateAlias: base.candidateAlias,
        displayName: base.displayName,
        originalFilename: base.originalFilename,
        score: Number.isFinite(base.score) ? base.score : 0,
        strengths: base.strengths || [],
        improvements: base.improvements || [],
        explanation: base.explanation || '',
        tagConflicts: base.tagConflicts,
        dimensionScores: base.dimensionScores,
        dimensionExplanations: base.dimensionExplanations,
        hasScore: Boolean(server || inlineScore),
      };
    })
      .sort((a, b) => {
        if ((a.hasScore ? 1 : 0) !== (b.hasScore ? 1 : 0)) return (b.hasScore ? 1 : 0) - (a.hasScore ? 1 : 0);
        const scoreA = Number.isFinite(a.score) ? a.score : 0;
        const scoreB = Number.isFinite(b.score) ? b.score : 0;
        if (scoreA !== scoreB) return scoreB - scoreA;
        return displayCandidateName(a, revealIdentity).localeCompare(displayCandidateName(b, revealIdentity));
      })
      .map((row, index) => ({ ...row, rank: index + 1 }));
  }, [linkedResumes, rankingByResumeId, jobId, revealIdentity]);

  const effectiveRankings = linkedCandidateRows;
  const isInlineOnly = !rankings.some(r => r.resumeId && linkedResumeIds.has(r.resumeId));

  const handleUnlink = useCallback(async (resumeId: string) => {
    if (!resumeId || !jobId) return;
    setUnlinking(resumeId);
    try {
      const res = await authFetch(`/api/resumes/${resumeId}/link-jd/${jobId}`, { method: 'DELETE' });
      if (res.ok) onCandidateLinkageChanged();
    } catch { /* ignore */ } finally {
      setUnlinking(null);
    }
  }, [authFetch, jobId, onCandidateLinkageChanged]);

  const sorted = useMemo(() => [...effectiveRankings].sort((a, b) => b.score - a.score), [effectiveRankings]);

  const candidateKey = useCallback((r: MatchRanking) => r.resumeId || r.resumeFilename, []);

  useEffect(() => {
    if (sorted.length === 0) {
      setSelectedCandidateKey(null);
      return;
    }
    if (!selectedCandidateKey || !sorted.some(r => candidateKey(r) === selectedCandidateKey)) {
      setSelectedCandidateKey(candidateKey(sorted[0]));
    }
  }, [sorted, selectedCandidateKey, candidateKey]);

  const selectedCandidate = useMemo(() => {
    if (sorted.length === 0) return null;
    return sorted.find(r => candidateKey(r) === selectedCandidateKey) || sorted[0];
  }, [sorted, selectedCandidateKey, candidateKey]);

  const handleExportPDF = useCallback(async () => {
    if (sorted.length === 0) return;

    // Capture charts as image
    let chartsImg = '';
    if (chartsRef.current) {
      try {
        const canvas = await html2canvas(chartsRef.current, { backgroundColor: '#ffffff', scale: 2 });
        // Keep payload below API request limits while preserving readability.
        const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
        chartsImg = dataUrl.length <= 8_000_000 ? dataUrl : '';
      } catch { /* skip charts */ }
    }

    const slug = (jdTitle || 'linked-candidates')
      .replace(/[^A-Za-z0-9 _-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .toLowerCase() || 'linked-candidates';

    const heatmap = buildReportHeatmapPayload(
      dimensionWeights,
      sorted,
      (candidate) => displayCandidateName(candidate, revealIdentity),
    );

    const reportPayload = {
      reportType: 'linked_candidates',
      title: 'Candidate Rankings',
      subtitle: `${jdTitle} — ${sorted.length} candidate${sorted.length !== 1 ? 's' : ''}`,
      summaryMetrics: [
        { label: 'Candidates', value: String(sorted.length) },
        { label: 'Mode', value: 'Whole Pipeline' },
      ],
      rankingRows: sorted.map((r, i) => ({
        rank: i + 1,
        name: displayCandidateName(r, revealIdentity),
        score: Math.round(r.score),
      })),
      candidateDetails: sorted.map((r, i) => ({
        rank: i + 1,
        name: displayCandidateName(r, revealIdentity),
        score: Math.round(r.score),
        strengths: r.strengths || [],
        improvements: r.improvements || [],
        analysis: r.explanation || '',
      })),
      heatmap,
      chartsImageBase64: chartsImg || undefined,
      generatedBy: 'xRank',
      filename: `${slug}-report.pdf`,
    };

    try {
      const res = await authFetch('/api/reports/pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reportPayload),
      });
      if (!res.ok) {
        throw new Error(`Export failed with status ${res.status}`);
      }
      const blob = await res.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = reportPayload.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Report export failed', error);
      window.alert('Report export failed. Please try again.');
    }
  }, [sorted, jdTitle, revealIdentity, authFetch, dimensionWeights]);

  if (!jdIndexed) {
    return (
      <div className="v2-empty">
        <h3>Job Not Yet Indexed</h3>
        <p>This job description is still being processed. Matching will be available once indexing is complete.</p>
      </div>
    );
  }

  if (linkedResumes.length === 0) {
    return (
      <div>
        <div className="v2-empty">
          <h3>No Candidates</h3>
          <p>{readOnly ? 'No candidates were linked to this role.' : 'Use Add Candidates to link resumes from your talent pool.'}</p>
          {!readOnly && canEdit && (
            <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
              <button className="v2-btn v2-btn-primary" onClick={() => onOpenAddCandidates('upload')}>
                + Add Candidates
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="v2-empty"><span className="v2-spinner" /> Matching candidates against this job...</div>
    );
  }

  if (effectiveRankings.length === 0) {
    const someProcessing = linkedResumes.some(r => r.status !== 'indexed');
    return (
      <div className="v2-empty">
        {someProcessing ? (
          <>
            <h3>Resumes Processing</h3>
            <p>Your {linkedResumes.length} resume{linkedResumes.length !== 1 ? 's are' : ' is'} being enriched in the background. Scores will appear here as each one finishes.</p>
          </>
        ) : ran ? (
          <>
            <h3>No Matches Found</h3>
            <p>{linkedResumes.length} resume{linkedResumes.length !== 1 ? 's are' : ' is'} linked but none could be scored.</p>
            <button className="v2-btn v2-btn-secondary" onClick={onRunMatch} disabled={loading}>Retry</button>
          </>
        ) : (
          <>
            <h3>No Scores Yet</h3>
            <p>{linkedResumes.length} resume{linkedResumes.length !== 1 ? 's are' : ' is'} linked.</p>
            <button className="v2-btn v2-btn-primary" onClick={onRunMatch} disabled={loading}>Run Match</button>
          </>
        )}
      </div>
    );
  }

  const pipelineHeatmapCandidates = sorted
    .filter(r => r.dimensionScores && Object.keys(r.dimensionScores).length > 0)
    .map((r) => ({
      name: displayCandidateName(r, revealIdentity),
      resumeFilename: displayCandidateName(r, revealIdentity),
      score: r.score,
      dimensionScores: r.dimensionScores!,
      dimensionExplanations: r.dimensionExplanations,
    }));

  const focusedChartCandidates = selectedCandidate && selectedCandidate.dimensionScores
    ? [{
        name: displayCandidateName(selectedCandidate, revealIdentity),
        resumeFilename: selectedCandidate.resumeFilename,
        score: selectedCandidate.score,
        dimensionScores: selectedCandidate.dimensionScores,
        dimensionExplanations: selectedCandidate.dimensionExplanations,
      }]
    : [];

  return (
    <div>
      <div className="linked-candidates-toolbar">
        <span>
          {sorted.length} candidate{sorted.length !== 1 ? 's' : ''} {isInlineOnly ? 'scored' : 'ranked'}
        </span>
        <div className="linked-candidates-toolbar__actions">
          {!readOnly && canEdit && (
            <button className="v2-btn v2-btn-primary" onClick={() => onOpenAddCandidates('upload')}>
              + Add Candidates
            </button>
          )}
          <button className="v2-btn v2-btn-secondary" onClick={handleExportPDF} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <IconDownload /> Export Report
          </button>
        </div>
      </div>

      <div className="linked-candidates-panes linked-candidates-panes--whole" ref={chartsRef}>
        <section className="linked-candidates-pane linked-candidates-pane--insight linked-candidates-pane--analytics">
          <div className="linked-candidates-pane__header">
            <span>Fit Analysis</span>
            <h3>{selectedCandidate ? displayCandidateName(selectedCandidate, revealIdentity) : 'Selected Resume'}</h3>
          </div>

          {dimensionWeights && Object.keys(dimensionWeights).length > 0 && (
            <div className="linked-candidates-card">
              <DimensionRadarChart
                dimensionWeights={dimensionWeights}
                candidates={focusedChartCandidates}
                displayResumeName={displayResumeName}
                showCandidateControls={false}
                forceShowAll={false}
                showCopyAction={false}
              />
            </div>
          )}
        </section>

        <section className="linked-candidates-pane linked-candidates-pane--detail linked-candidates-pane--board">
          <div className="linked-candidates-pane__header">
            <h3>All Candidates</h3>
          </div>

          <div className="linked-candidates-whole-list">
            {sorted.map(r => {
              const rowKey = candidateKey(r);
              return (
                <CandidateRow
                  key={rowKey}
                  ranking={r}
                  isExpanded={expanded === rowKey}
                  onToggle={() => {
                    setSelectedCandidateKey(rowKey);
                    onExpand(expanded === rowKey ? null : rowKey);
                  }}
                  authFetch={authFetch}
                  revealIdentity={revealIdentity}
                  unlinkAction={!readOnly && canEdit && r.resumeId ? {
                    onUnlink: () => handleUnlink(r.resumeId),
                    unlinking: unlinking === r.resumeId,
                  } : undefined}
                />
              );
            })}
          </div>
        </section>
      </div>

      {dimensionWeights && Object.keys(dimensionWeights).length > 0 && pipelineHeatmapCandidates.length > 0 && (
        <section className="linked-candidates-fitmap-full">
          <DimensionHeatmap
            dimensionWeights={dimensionWeights}
            candidates={pipelineHeatmapCandidates}
            displayResumeName={(name) => name}
            maxCandidates={UI_HEATMAP_MAX_CANDIDATES}
            showCopyAction={false}
          />
        </section>
      )}
    </div>
  );
}

/* ── Discover Tab ── */
const IconDownload = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function DiscoverTab({
  results, filtered, loading, ran, expanded, onExpand, topN, onTopNChange, onRun, jdIndexed, authFetch, jobId, jdTitle, linkedResumeIds, metadata, processingTime, cached, onNavigateCandidates, onLinked, hasBulkInProgress, dimensionWeights, revealIdentity, compact,
}: {
  results: MatchRanking[];
  filtered: MatchRanking[];
  loading: boolean;
  ran: boolean;
  expanded: string | null;
  onExpand: (f: string | null) => void;
  topN: number;
  onTopNChange: (n: number) => void;
  onRun: () => void;
  jdIndexed: boolean;
  authFetch: ReturnType<typeof useAuthFetch>;
  jobId: string;
  jdTitle: string;
  linkedResumeIds: Set<string>;
  metadata: { avgScore: number; totalCandidates: number } | null;
  processingTime: number | null;
  cached: boolean;
  onNavigateCandidates: () => void;
  onLinked: (resumeId: string) => void;
  hasBulkInProgress?: boolean;
  dimensionWeights: Record<string, number>;
  revealIdentity: boolean;
  compact?: boolean;
}) {
  const [linking, setLinking] = useState<string | null>(null);
  const [newlyLinked, setNewlyLinked] = useState<Set<string>>(new Set());
  const [showFiltered, setShowFiltered] = useState(false);
  const [reviewResumeId, setReviewResumeId] = useState<string | null>(null);
  const chartsRef = useRef<HTMLDivElement>(null);

  const isAlreadyLinked = (resumeId: string) => linkedResumeIds.has(resumeId) || newlyLinked.has(resumeId);

  const linkToJob = async (resumeId: string) => {
    if (!resumeId || !jobId) return;
    setLinking(resumeId);
    try {
      const res = await authFetch(`/api/resumes/${resumeId}/link-jd`, {
        method: 'POST',
        body: JSON.stringify({ jdId: jobId }),
      });
      if (res.ok) {
        setNewlyLinked(prev => new Set(prev).add(resumeId));
        onLinked(resumeId);
      }
    } catch { /* ignore */ } finally {
      setLinking(null);
    }
  };

  const normalizedResults = useMemo(
    () => results.map(r => ({ ...r, resumeFilename: displayCandidateName(r, revealIdentity) })),
    [results, revealIdentity],
  );
  const normalizedFiltered = useMemo(
    () => filtered.map(r => ({ ...r, resumeFilename: displayCandidateName(r, revealIdentity) })),
    [filtered, revealIdentity],
  );
  const sorted = [...normalizedResults].sort((a, b) => b.score - a.score);
  const reviewedCandidate = useMemo(
    () => sorted.find(r => r.resumeId === reviewResumeId) || null,
    [sorted, reviewResumeId],
  );

  useEffect(() => {
    if (sorted.length === 0) {
      setReviewResumeId(null);
      return;
    }
    if (!reviewResumeId || !sorted.some(r => r.resumeId === reviewResumeId)) {
      setReviewResumeId(sorted[0].resumeId || null);
    }
  }, [sorted, reviewResumeId]);

  const handleExportPDF = useCallback(async () => {
    if (sorted.length === 0) return;

    // Capture charts as image
    let chartsImg = '';
    if (chartsRef.current) {
      try {
        const canvas = await html2canvas(chartsRef.current, { backgroundColor: '#ffffff', scale: 2 });
        // Keep payload below API request limits while preserving readability.
        const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
        chartsImg = dataUrl.length <= 8_000_000 ? dataUrl : '';
      } catch { /* skip charts */ }
    }

    const slug = (jdTitle || 'discover-results')
      .replace(/[^A-Za-z0-9 _-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .toLowerCase() || 'discover-results';

    const heatmap = buildReportHeatmapPayload(
      dimensionWeights,
      sorted,
      (candidate) => displayResumeName(candidate.resumeFilename),
    );

    const reportPayload = {
      reportType: 'discover',
      title: 'Discover Results',
      subtitle: `${jdTitle} — Top ${sorted.length} from talent pool`,
      summaryMetrics: [
        { label: 'Matched', value: String(sorted.length) },
        { label: 'Average Score', value: metadata?.avgScore != null ? `${Math.round(metadata.avgScore)}%` : '—' },
        { label: 'Processing', value: processingTime ? `${(processingTime / 1000).toFixed(1)}s` : '—' },
        ...(normalizedFiltered.length > 0 ? [{ label: 'Filtered by Tags', value: String(normalizedFiltered.length) }] : []),
      ],
      rankingRows: sorted.map((r, i) => ({
        rank: i + 1,
        name: displayResumeName(r.resumeFilename),
        score: Math.round(r.score),
      })),
      candidateDetails: sorted.map((r, i) => ({
        rank: i + 1,
        name: displayResumeName(r.resumeFilename),
        score: Math.round(r.score),
        strengths: r.strengths || [],
        improvements: r.improvements || [],
        analysis: r.explanation || '',
        statusBadge: isAlreadyLinked(r.resumeId) ? 'Linked' : undefined,
      })),
      filteredRows: normalizedFiltered.map((r, i) => ({
        rank: i + 1,
        name: displayResumeName(r.resumeFilename),
        score: Math.round(r.score),
        reason: (r.tagConflicts || []).join('; '),
      })),
      heatmap,
      chartsImageBase64: chartsImg || undefined,
      generatedBy: 'xRank',
      filename: `${slug}-report.pdf`,
    };

    try {
      const res = await authFetch('/api/reports/pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reportPayload),
      });
      if (!res.ok) {
        throw new Error(`Export failed with status ${res.status}`);
      }
      const blob = await res.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = reportPayload.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Report export failed', error);
      window.alert('Report export failed. Please try again.');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorted, normalizedFiltered, jdTitle, metadata, processingTime, authFetch, dimensionWeights]);

  if (!jdIndexed) {
    return (
      <div className="v2-empty">
        <h3>Job Not Yet Indexed</h3>
        <p>Discover requires the job to be indexed first.</p>
      </div>
    );
  }

  const linkedCount = sorted.filter(r => isAlreadyLinked(r.resumeId)).length;

  return (
    <div>
      {hasBulkInProgress && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius-md)', padding: 'var(--space-2) var(--space-3)', marginBottom: 'var(--space-4)', fontSize: 'var(--font-size-sm)', color: 'var(--color-warning, #b45309)' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          A bulk import is in progress — talent pool results may be incomplete until it finishes.
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-4)', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)' }}>
          Match your entire talent pool against this job description.
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          {newlyLinked.size > 0 && (
            <button className="v2-btn v2-btn-secondary" onClick={onNavigateCandidates}>
              View Candidates ({newlyLinked.size} new)
            </button>
          )}
          <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            Top
            <select
              value={topN}
              onChange={e => onTopNChange(Number(e.target.value))}
              style={{ marginLeft: 4, padding: '2px 4px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 'var(--font-size-xs)' }}
            >
              {[3, 5, 8, 10].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <button className="v2-btn v2-btn-primary" onClick={onRun} disabled={loading}>
            {loading ? <><span className="v2-spinner" /> Searching...</> : <><IconSearch /> Discover</>}
          </button>
        </div>
      </div>

      {loading && (
        <div className="v2-empty"><span className="v2-spinner" /> Scanning talent pool...</div>
      )}

      {!loading && ran && results.length === 0 && (
        <div className="v2-empty">
          <h3>No Matches Found</h3>
          <p>No resumes in your talent pool matched well enough for this job description.</p>
        </div>
      )}

      {!loading && sorted.length > 0 && (
        <div>
          {reviewedCandidate && (
            <div className="v2-card" style={{ marginBottom: compact ? 'var(--space-3)' : 'var(--space-4)' }}>
              <div className="v2-card-header">
                <h2>
                  Review: {displayCandidateName(reviewedCandidate, revealIdentity)}
                </h2>
                <span className={`v2-score ${scoreClass(reviewedCandidate.score)}`} style={{ fontSize: 'var(--font-size-xs)' }}>
                  {Math.round(reviewedCandidate.score)}%
                </span>
              </div>
              <div className="v2-card-body">
                {reviewedCandidate.dimensionScores && Object.keys(reviewedCandidate.dimensionScores).length > 0 ? (
                  <DimensionRadarChart
                    dimensionWeights={dimensionWeights}
                    candidates={[{
                      name: displayResumeName(reviewedCandidate.resumeFilename),
                      resumeFilename: reviewedCandidate.resumeFilename,
                      score: reviewedCandidate.score,
                      dimensionScores: reviewedCandidate.dimensionScores,
                    }]}
                    displayResumeName={displayResumeName}
                  />
                ) : (
                  <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>
                    Dimension-level breakdown is still processing for this candidate. You can still link now and review full diagnostics once indexed.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Metrics */}
          <div className="v2-metrics" style={{ marginBottom: 'var(--space-4)' }}>
            <div className="v2-metric-card">
              <span className="v2-metric-value">{sorted.length}</span>
              <span className="v2-metric-label">Matched</span>
            </div>
            <div className="v2-metric-card">
              <span className="v2-metric-value">{metadata?.avgScore ?? '—'}</span>
              <span className="v2-metric-label">Avg Score</span>
            </div>
            <div className="v2-metric-card">
              <span className="v2-metric-value">{processingTime ? `${(processingTime / 1000).toFixed(1)}s` : '—'}</span>
              <span className="v2-metric-label">Processing</span>
            </div>
            {linkedCount > 0 && (
              <div className="v2-metric-card">
                <span className="v2-metric-value">{linkedCount}</span>
                <span className="v2-metric-label">Already Linked</span>
              </div>
            )}
            {normalizedFiltered.length > 0 && (
              <div className="v2-metric-card">
                <span className="v2-metric-value">{normalizedFiltered.length}</span>
                <span className="v2-metric-label">Filtered by Tags</span>
              </div>
            )}
            {cached && (
              <div className="v2-metric-card">
                <span className="v2-metric-value">⚡</span>
                <span className="v2-metric-label">Cached</span>
              </div>
            )}
          </div>

          <div ref={chartsRef}>
          {/* Dimension Radar Chart */}
          {dimensionWeights && Object.keys(dimensionWeights).length > 0 && (
            <div className="v2-card" style={{ marginBottom: 'var(--space-4)' }}>
              <div className="v2-card-body">
                <DimensionRadarChart
                  dimensionWeights={dimensionWeights}
                  candidates={sorted.filter(r => r.dimensionScores && Object.keys(r.dimensionScores).length > 0).map(r => ({
                    name: displayResumeName(r.resumeFilename),
                    resumeFilename: r.resumeFilename,
                    score: r.score,
                    dimensionScores: r.dimensionScores!,
                  }))}
                  displayResumeName={displayResumeName}
                  showCopyAction={false}
                />
              </div>
            </div>
          )}
          {/* Fit Heatmap */}
          {dimensionWeights && Object.keys(dimensionWeights).length > 0 && (
            <div className="v2-card" style={{ marginBottom: 'var(--space-4)' }}>
              <div className="v2-card-body">
                <DimensionHeatmap
                  dimensionWeights={dimensionWeights}
                  candidates={sorted.filter(r => r.dimensionScores && Object.keys(r.dimensionScores).length > 0).map(r => ({
                    name: displayResumeName(r.resumeFilename),
                    resumeFilename: r.resumeFilename,
                    score: r.score,
                    dimensionScores: r.dimensionScores!,
                  }))}
                  displayResumeName={displayResumeName}
                  maxCandidates={UI_HEATMAP_MAX_CANDIDATES}
                  showCopyAction={false}
                />
              </div>
            </div>
          )}
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
            <button className="v2-btn v2-btn-secondary" onClick={handleExportPDF} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <IconDownload /> Export Report
            </button>
          </div>

          {/* Detail list */}
          <div className="v2-card" style={{ marginBottom: 'var(--space-4)' }}>
            <div className="v2-card-header">
              <h2>Candidates ({sorted.length})</h2>
            </div>
            <div className="v2-card-body-flush">
              {sorted.map(r => {
                const alreadyLinked = isAlreadyLinked(r.resumeId);
                return (
                  <CandidateRow
                    key={r.resumeFilename}
                    ranking={r}
                    isExpanded={expanded === r.resumeFilename}
                    onToggle={() => onExpand(expanded === r.resumeFilename ? null : r.resumeFilename)}
                    authFetch={authFetch}
                    revealIdentity={revealIdentity}
                    reviewAction={{
                      onReview: () => {
                        setReviewResumeId(r.resumeId || null);
                        onExpand(r.resumeFilename);
                      },
                    }}
                    linkAction={r.resumeId ? {
                      onLink: () => linkToJob(r.resumeId),
                      linking: linking === r.resumeId,
                      linked: alreadyLinked,
                    } : undefined}
                  />
                );
              })}
            </div>
          </div>

          {/* Filtered section */}
          {normalizedFiltered.length > 0 && (
            <div className="v2-card">
              <div className="v2-card-header" style={{ cursor: 'pointer' }} onClick={() => setShowFiltered(!showFiltered)}>
                <h2>Also Qualified — Filtered by Tags ({normalizedFiltered.length})</h2>
                <span style={{ transform: showFiltered ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', display: 'flex' }}>
                  <IconChevron />
                </span>
              </div>
              {showFiltered && (
                <div className="v2-card-body-flush">
                  <table className="v2-table">
                    <thead>
                      <tr>
                        <th style={{ width: 60 }}>Score</th>
                        <th>Candidate</th>
                        <th>Reason Filtered</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...normalizedFiltered].sort((a, b) => b.score - a.score).map(r => (
                        <tr key={r.resumeFilename}>
                          <td>
                            <span className={`v2-score ${scoreClass(r.score)}`} style={{ fontSize: 'var(--font-size-xs)' }}>
                              {Math.round(r.score)}%
                            </span>
                          </td>
                          <td
                            title={displayResumeName(r.resumeFilename)}
                            style={{ fontWeight: 500, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          >
                            {displayResumeName(r.resumeFilename)}
                          </td>
                          <td
                            title={(r.tagConflicts || []).join('; ')}
                            style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', maxWidth: 520, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          >
                            {(r.tagConflicts || []).join('; ')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!ran && !loading && (
        <div className="v2-empty">
          <div className="v2-empty-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </div>
          <h3>Discover top matches</h3>
          <p>Search your entire talent pool against this job. Already-linked candidates will appear with a "Linked" badge.</p>
        </div>
      )}
    </div>
  );
}

function AddCandidatesModal({
  section,
  onSectionChange,
  onClose,
  results,
  loading,
  ran,
  topN,
  onTopNChange,
  onRun,
  applyTagFilter,
  onApplyTagFilterChange,
  canUseTagFilter,
  jdIndexed,
  authFetch,
  jobId,
  linkedResumeIds,
  ineligibleResumeIds,
  revealIdentity,
  onLinked,
  onStatus,
  onViewLinked,
}: {
  section: 'upload' | 'talent';
  onSectionChange: (section: 'upload' | 'talent') => void;
  onClose: () => void;
  results: MatchRanking[];
  loading: boolean;
  ran: boolean;
  topN: number;
  onTopNChange: (n: number) => void;
  onRun: () => void;
  applyTagFilter: boolean;
  onApplyTagFilterChange: (value: boolean) => void;
  canUseTagFilter: boolean;
  jdIndexed: boolean;
  authFetch: ReturnType<typeof useAuthFetch>;
  jobId: string;
  linkedResumeIds: Set<string>;
  ineligibleResumeIds: Set<string>;
  revealIdentity: boolean;
  onLinked: (resumeId: string) => void;
  onStatus: (message: string) => void;
  onViewLinked: () => void;
}) {
  type AddCandidateSource = 'upload' | 'talent';
  type FlowMode = 'initial' | 'list';
  type StagedCandidate = {
    resumeId: string;
    displayName: string;
    subtitle: string;
    score: number | null;
    source: AddCandidateSource;
    alreadyLinked: boolean;
  };

  const [mode, setMode] = useState<FlowMode>('initial');
  const [source, setSource] = useState<AddCandidateSource>(section);
  const [stagedCandidates, setStagedCandidates] = useState<StagedCandidate[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [uploading, setUploading] = useState(false);
  const [uploadLimitError, setUploadLimitError] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [talentRequested, setTalentRequested] = useState(false);
  const [linking, setLinking] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setSource(section);
  }, [section]);

  const upsertStaged = useCallback((incoming: StagedCandidate[]) => {
    setStagedCandidates(prev => {
      const byId = new Map(prev.map(item => [item.resumeId, item]));
      incoming.forEach(item => {
        const existing = byId.get(item.resumeId);
        if (!existing) {
          byId.set(item.resumeId, item);
          return;
        }
        byId.set(item.resumeId, {
          ...existing,
          ...item,
          score: existing.score ?? item.score,
          alreadyLinked: existing.alreadyLinked || item.alreadyLinked,
        });
      });
      return Array.from(byId.values()).sort((a, b) => (b.score ?? -1) - (a.score ?? -1));
    });
  }, []);

  const resetToInitial = useCallback((message?: string) => {
    setMode('initial');
    setStagedCandidates([]);
    setSelectedIds(new Set());
    setTalentRequested(false);
    if (message) {
      setNotice(message);
      window.setTimeout(() => setNotice(null), 3500);
    }
  }, []);

  const stageFromTalent = useCallback(() => {
    const normalized = [...results]
      .filter(r => !!r.resumeId && !ineligibleResumeIds.has(r.resumeId))
      .sort((a, b) => b.score - a.score)
      .map(r => ({
        resumeId: r.resumeId,
        displayName: displayCandidateName(r, revealIdentity),
        subtitle: r.explanation || 'Top candidate from talent pool shortlist',
        score: typeof r.score === 'number' ? r.score : null,
        source: 'talent' as AddCandidateSource,
        alreadyLinked: linkedResumeIds.has(r.resumeId),
      }));

    if (normalized.length === 0) {
      setNotice('No shortlist candidates found for this role.');
      window.setTimeout(() => setNotice(null), 3000);
      return;
    }

    upsertStaged(normalized);
    setSelectedIds(new Set());
    setMode('list');
  }, [results, revealIdentity, linkedResumeIds, ineligibleResumeIds, upsertStaged]);

  useEffect(() => {
    if (source !== 'talent' || !talentRequested) return;
    if (loading || !ran) return;
    stageFromTalent();
    setTalentRequested(false);
  }, [source, talentRequested, loading, ran, stageFromTalent]);

  const uploadFailureMessage = useCallback(async (res: Response, file?: File): Promise<string> => {
    const fallback = file
      ? `${file.name}: upload failed (HTTP ${res.status})`
      : `Upload failed (HTTP ${res.status})`;
    try {
      const body = await res.json();
      const detail = body?.detail;
      if (typeof detail === 'string' && detail.trim()) return detail;
      if (!detail || typeof detail !== 'object') return fallback;

      const reason = detail.reason ? `reason=${detail.reason}` : '';
      const fileName = detail.file?.filename || file?.name || 'file';
      const fileType = detail.file?.contentType || file?.type || 'unknown';
      const sizeBytes = Number(detail.file?.sizeBytes ?? file?.size ?? 0);
      const maxBytes = Number(detail.limits?.maxFileSizeBytes ?? 0);
      const accepted = Array.isArray(detail.limits?.acceptedExtensions)
        ? detail.limits.acceptedExtensions.join(', ')
        : '.pdf, .docx';
      const message = detail.message || 'Upload failed';
      const sizePart = sizeBytes > 0 ? `uploaded=${toMb(sizeBytes)} MB` : '';
      const maxPart = maxBytes > 0 ? `max=${toMb(maxBytes)} MB` : '';
      const extras = [reason, `type=${fileType}`, sizePart, maxPart, `allowed=${accepted}`]
        .filter(Boolean)
        .join(', ');
      return `${fileName}: ${message}${extras ? ` (${extras})` : ''}`;
    } catch {
      return fallback;
    }
  }, []);

  const uploadToGlobal = useCallback(async (files: File[]) => {
    const { validFiles, issues } = await validateResumeFiles(files);
    if (validFiles.length === 0 && issues.length > 0) {
      setNotice(`No files were accepted. ${issues.length} file(s) failed validation.`);
      window.setTimeout(() => setNotice(null), 5000);
      return;
    }

    setSource('upload');
    onSectionChange('upload');

    if (validFiles.length > BULK_UPLOAD_MAX_FILES) {
      setUploadLimitError(validFiles.length);
      setNotice(`Bulk upload is capped at ${BULK_UPLOAD_MAX_FILES} files. Use Migration Center for larger imports.`);
      window.setTimeout(() => setUploadLimitError(null), 3000);
      window.setTimeout(() => setNotice(null), 5000);
      return;
    }

    setUploading(true);
    setNotice(null);
    const uploaded: StagedCandidate[] = [];
    let failed = 0;
    const failureDetails: string[] = [];

    if (validFiles.length > DIRECT_UPLOAD_MAX_FILES) {
      try {
        const filesPayload = await Promise.all(
          validFiles.map(async (file) => ({
            filename: file.name,
            contentType: file.type || 'application/octet-stream',
            hash: await (async () => {
              const buffer = await file.arrayBuffer();
              const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
              return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
            })(),
            size: file.size,
          }))
        );

        const initRes = await authFetch('/api/batch-upload-init', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ files: filesPayload, jdId: jobId }),
        });
        if (!initRes.ok) {
          const msg = await uploadFailureMessage(initRes);
          throw new Error(msg);
        }

        const initData = await initRes.json();
        const uploads: Array<{ clientIndex: number; resumeId: string; uploadUrl: string; contentType: string }> = initData.uploads ?? [];
        const duplicates: Array<{ resumeId: string; filename?: string }> = initData.duplicates ?? [];

        for (const duplicate of duplicates) {
          if (!duplicate.resumeId) continue;
          uploaded.push({
            resumeId: duplicate.resumeId,
            displayName: revealIdentity
              ? displayResumeName(duplicate.filename || duplicate.resumeId)
              : fallbackCandidateAlias(duplicate.resumeId),
            subtitle: 'Already in talent pool; linked to this job',
            score: null,
            source: 'upload',
            alreadyLinked: true,
          });
        }

        const successResumeIds: string[] = [];
        const CONCURRENCY = 4;
        for (let i = 0; i < uploads.length; i += CONCURRENCY) {
          const chunk = uploads.slice(i, i + CONCURRENCY);
          const results = await Promise.allSettled(
            chunk.map(async u => {
              const file = validFiles[u.clientIndex];
              if (!file) throw new Error('missing file mapping');
              const putRes = await fetch(u.uploadUrl, {
                method: 'PUT',
                headers: { 'Content-Type': u.contentType || file.type || 'application/octet-stream' },
                body: file,
              });
              if (!putRes.ok) throw new Error(`${file.name}: signed upload failed (HTTP ${putRes.status})`);
              successResumeIds.push(u.resumeId);
              uploaded.push({
                resumeId: u.resumeId,
                displayName: revealIdentity
                  ? displayResumeName(file.name)
                  : fallbackCandidateAlias(u.resumeId),
                subtitle: 'Uploaded and queued; auto-linked to this job',
                score: null,
                source: 'upload',
                alreadyLinked: true,
              });
            })
          );
          for (const r of results) {
            if (r.status === 'rejected') {
              failed += 1;
              failureDetails.push(r.reason instanceof Error ? r.reason.message : String(r.reason));
            }
          }
        }

        if (uploads.length > 0) {
          const startRes = await authFetch(`/api/batch-start/${initData.batchId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uploadedResumeIds: successResumeIds }),
          });
          if (!startRes.ok) {
            const msg = await uploadFailureMessage(startRes);
            throw new Error(msg);
          }
        }

        setUploading(false);
        const summary = `Uploaded/linked ${uploaded.length} file${uploaded.length !== 1 ? 's' : ''}${failed + issues.length > 0 ? `, ${failed + issues.length} failed` : ''}.`;
        const details = [issues.length > 0 ? issuesToTooltip(issues) : '', failureDetails.join('\n')]
          .filter(Boolean)
          .join('\n');
        if (uploaded.length > 0) {
          window.dispatchEvent(new Event('usage:invalidate'));
          onStatus(details ? `${summary}\n${details}` : summary);
          onViewLinked();
          return;
        }

        setNotice(details ? `${summary}\n${details}` : summary);
        window.setTimeout(() => setNotice(null), 7000);
        return;
      } catch (e) {
        setUploading(false);
        setNotice(e instanceof Error ? e.message : 'Batch upload failed.');
        window.setTimeout(() => setNotice(null), 5000);
        return;
      }
    }

    for (const file of validFiles) {
      try {
        const formData = new FormData();
        formData.append('resume', file);
        formData.append('jd_id', jobId);
        const res = await authFetch('/api/upload-resume', { method: 'POST', body: formData });
        if (!res.ok) throw new Error(await uploadFailureMessage(res, file));
        const data = await res.json();
        if (!data.resumeId) throw new Error('missing resume id');
        uploaded.push({
          resumeId: data.resumeId,
          displayName: revealIdentity
            ? displayResumeName(data.originalFilename || data.filename || file.name)
            : fallbackCandidateAlias(data.resumeId),
          subtitle: data.deduplicated
            ? 'Already in talent pool; linked to this job'
            : 'Uploaded and linked to this job',
          score: null,
          source: 'upload',
          alreadyLinked: true,
        });
      } catch {
        failed += 1;
      }
    }

    setUploading(false);
    const clientIssueCount = issues.length;
    const summary = `Uploaded/linked ${uploaded.length} file${uploaded.length !== 1 ? 's' : ''}${failed + clientIssueCount > 0 ? `, ${failed + clientIssueCount} failed` : ''}.`;
    if (uploaded.length > 0) {
      window.dispatchEvent(new Event('usage:invalidate'));
      const tooltip = issuesToTooltip(issues);
      onStatus(tooltip ? `${summary}\n${tooltip}` : summary);
      onViewLinked();
      return;
    }

    if (failed > 0 || clientIssueCount > 0) {
      const tooltip = issuesToTooltip(issues);
      setNotice(`${summary}${tooltip ? `\n${tooltip}` : ''}`);
      window.setTimeout(() => setNotice(null), 5000);
    }
  }, [
    authFetch,
    jobId,
    onStatus,
    onSectionChange,
    onViewLinked,
    revealIdentity,
    uploadFailureMessage,
  ]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    uploadToGlobal(Array.from(e.target.files));
    e.target.value = '';
  }, [uploadToGlobal]);

  const handleDropUpload = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    uploadToGlobal(Array.from(e.dataTransfer.files));
  }, [uploadToGlobal]);

  const startTalentShortlist = useCallback(() => {
    if (!jdIndexed) {
      setNotice('Job is still indexing. Talent shortlist will be available shortly.');
      window.setTimeout(() => setNotice(null), 3000);
      return;
    }
    setSource('talent');
    onSectionChange('talent');
    setTalentRequested(true);
    onRun();
  }, [jdIndexed, onRun, onSectionChange]);

  const toggleSelection = (resumeId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(resumeId)) next.delete(resumeId);
      else next.add(resumeId);
      return next;
    });
  };

  const selectAllAvailable = () => {
    const ids = stagedCandidates.filter(c => !c.alreadyLinked).map(c => c.resumeId);
    setSelectedIds(new Set(ids));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const pushSelectedToJob = async () => {
    if (!jobId) return;
    const targetIds = Array.from(selectedIds).filter(id => {
      const candidate = stagedCandidates.find(c => c.resumeId === id);
      return !!candidate && !candidate.alreadyLinked;
    });

    if (targetIds.length === 0) {
      setNotice('Select at least one unlinked candidate.');
      window.setTimeout(() => setNotice(null), 2500);
      return;
    }

    setLinking(true);
    let success = 0;
    let failed = 0;
    for (const resumeId of targetIds) {
      try {
        const res = await authFetch(`/api/resumes/${resumeId}/link-jd`, {
          method: 'POST',
          body: JSON.stringify({ jdId: jobId }),
        });
        if (res.ok) {
          success += 1;
          onLinked(resumeId);
        } else {
          failed += 1;
        }
      } catch {
        failed += 1;
      }
    }
    setLinking(false);

    if (success > 0) {
      resetToInitial();
      onViewLinked();
      return;
    }

    if (success > 0) {
      setNotice(`${success} linked, ${failed} failed. You can retry.`);
    } else {
      setNotice('Failed to link selected candidates.');
    }
    window.setTimeout(() => setNotice(null), 3500);
  };

  const selectedCount = Array.from(selectedIds).length;

  return (
    ReactDOM.createPortal(<div className="v2-modal-overlay" onClick={onClose}>
      <div className="v2-modal add-candidates-modal" onClick={e => e.stopPropagation()}>
        <div className="v2-modal-header add-candidates-modal__header">
          <div>
            <h3>Add Candidates</h3>
            <p className="add-candidates-modal__subtitle">
              {mode === 'initial'
                ? 'Choose upload for instant linking or talent pool for manual selection.'
                : 'Review and select candidates to attach to this job.'}
            </p>
          </div>
          <div className="add-candidates-modal__header-actions">
            <span className="add-candidates-modal__selected">Selected: {selectedCount}</span>
            {mode === 'list' && (
              <button className="v2-btn v2-btn-primary" onClick={pushSelectedToJob} disabled={linking || selectedCount === 0}>
                {linking ? <><span className="v2-spinner" /> Linking...</> : 'Link Selected'}
              </button>
            )}
            <button className="v2-btn v2-btn-ghost" onClick={onClose}>Close</button>
          </div>
        </div>

        <div className="v2-modal-body add-candidates-modal__body">
          {notice && <div className="add-candidates-upload-alert">{notice}</div>}

          {mode === 'initial' && (
            <div className="add-candidates-source-grid">
              <div
                className={`add-candidates-source-card add-candidates-upload-dropzone ${dragActive ? 'active' : ''}`}
                onDragOver={e => { e.preventDefault(); setDragActive(true); }}
                onDragEnter={() => setDragActive(true)}
                onDragLeave={() => setDragActive(false)}
                onDrop={handleDropUpload}
              >
                <input ref={fileInputRef} type="file" multiple accept=".pdf,.docx" onChange={handleFileInput} style={{ display: 'none' }} />
                <div className="add-candidates-source-card__header">
                  <h4>Upload New Resumes</h4>
                </div>
                <p>Upload resumes and auto-link them to this job in one step.</p>
                <button className="v2-btn v2-btn-primary" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
                  {uploading ? <><span className="v2-spinner" /> Uploading...</> : 'Upload & Link to Job'}
                </button>
                <small>PDF or DOCX, up to 200 files per upload.</small>
                {uploadLimitError !== null && (
                  <div className="add-candidates-upload-alert error">
                    Too many files ({uploadLimitError}). Maximum is 200 files per upload.
                  </div>
                )}
              </div>

              <div className="add-candidates-source-card">
                <div className="add-candidates-source-card__header">
                  <h4>Pick from Talent Pool</h4>
                  <div>
                    <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                      Top
                      <select
                        value={topN}
                        onChange={e => onTopNChange(Number(e.target.value))}
                        style={{ marginLeft: 6, padding: '2px 6px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 'var(--font-size-xs)' }}
                      >
                        {[5, 8, 10, 15].map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </label>
                    <label style={{ marginLeft: 10, fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                      <input
                        type="checkbox"
                        checked={canUseTagFilter && applyTagFilter}
                        disabled={!canUseTagFilter}
                        onChange={(e) => onApplyTagFilterChange(e.target.checked)}
                        style={{ marginRight: 6 }}
                      />
                      Tag constraints (Pro+)
                    </label>
                  </div>
                </div>

                <p>Fetch the top shortlist for this role and select who should be linked.</p>

                <button className="v2-btn v2-btn-secondary" onClick={startTalentShortlist} disabled={loading || !jdIndexed}>
                  {loading ? <><span className="v2-spinner" /> Loading shortlist...</> : 'Get Top Candidates'}
                </button>
                {!jdIndexed && <small>Available once the job finishes indexing.</small>}
                {canUseTagFilter && applyTagFilter && (
                  <small>Tag constraints ON. Limit: 20 runs per role per month.</small>
                )}

                {results.length > 0 && (
                  <div className="add-candidates-preview-list">
                    {[...results].filter(r => !ineligibleResumeIds.has(r.resumeId)).sort((a, b) => b.score - a.score).slice(0, 4).map(r => (
                      <div key={r.resumeId || r.resumeFilename}>
                        <span>{displayCandidateName(r, revealIdentity)}</span>
                        <span className={`v2-score ${scoreClass(r.score)}`}>{Math.round(r.score)}%</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {mode === 'list' && (
            <div className="add-candidates-list-view">
              <div className="add-candidates-list-toolbar">
                <div>
                  <strong>{stagedCandidates.length} candidate{stagedCandidates.length !== 1 ? 's' : ''} ready for review</strong>
                  <p>Source: {source === 'upload' ? 'Uploaded resumes' : 'Talent pool shortlist'}</p>
                </div>
                <div>
                  <button className="v2-btn v2-btn-ghost" onClick={selectAllAvailable}>Select all</button>
                  <button className="v2-btn v2-btn-ghost" onClick={clearSelection}>Clear</button>
                </div>
              </div>

              <div className="add-candidates-list-table">
                {stagedCandidates.map(candidate => {
                  const checked = selectedIds.has(candidate.resumeId);
                  return (
                    <label key={candidate.resumeId} className={`add-candidates-list-row ${checked ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={candidate.alreadyLinked}
                        onChange={() => toggleSelection(candidate.resumeId)}
                      />
                      <div className="add-candidates-list-row__main">
                        <strong title={candidate.displayName}>{candidate.displayName}</strong>
                        <span>{candidate.subtitle}</span>
                      </div>
                      <div className="add-candidates-list-row__meta">
                        {candidate.alreadyLinked ? (
                          <span className="v2-badge v2-badge-success">Linked</span>
                        ) : (
                          <span className="v2-badge v2-badge-muted">Not Linked</span>
                        )}
                        {candidate.score != null ? (
                          <span className={`v2-score ${scoreClass(candidate.score)}`}>{Math.round(candidate.score)}%</span>
                        ) : (
                          <span className="v2-badge v2-badge-muted">New</span>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="v2-modal-footer add-candidates-modal__footer">
          <span className="add-candidates-modal__footer-note">
            Choose candidates above, then push them directly into Linked Candidates.
          </span>
        </div>
      </div>
    </div>, document.body)
  );
}

/* ── Candidate Row (shared between Candidates & Discover) ── */
function CandidateRow({ ranking, isExpanded, onToggle, authFetch, revealIdentity, reviewAction, linkAction, unlinkAction, selectAction }: {
  ranking: MatchRanking;
  isExpanded: boolean;
  onToggle: () => void;
  authFetch: ReturnType<typeof useAuthFetch>;
  revealIdentity: boolean;
  reviewAction?: { onReview: () => void };
  linkAction?: { onLink: () => void; linking: boolean; linked: boolean };
  unlinkAction?: { onUnlink: () => void; unlinking: boolean };
  selectAction?: { selected: boolean; onToggleSelect: () => void };
}) {
  const [enrichment, setEnrichment] = useState<EnrichmentData | null>(null);
  const [enrichLoading, setEnrichLoading] = useState(false);
  const [enrichOpen, setEnrichOpen] = useState(false);

  const displayName = displayCandidateName(ranking, revealIdentity);

  const loadEnrichment = async () => {
    if (enrichment || !ranking.resumeId) return;
    setEnrichLoading(true);
    try {
      const res = await authFetch(`/api/resumes/${ranking.resumeId}/enrichment`);
      if (res.ok) setEnrichment(await res.json());
    } catch { /* ignore */ } finally {
      setEnrichLoading(false);
    }
  };

  const handleEnrichToggle = () => {
    if (!enrichOpen) loadEnrichment();
    setEnrichOpen(!enrichOpen);
  };

  return (
    <>
      <div className="v2-candidate-row" onClick={onToggle}>
        {selectAction && (
          <input
            type="checkbox"
            checked={selectAction.selected}
            onChange={e => {
              e.stopPropagation();
              selectAction.onToggleSelect();
            }}
            onClick={e => e.stopPropagation()}
            aria-label={`Select ${displayName}`}
            style={{ width: 16, height: 16, accentColor: 'var(--color-primary)', flexShrink: 0 }}
          />
        )}
        <div className={`v2-score ${scoreClass(ranking.score)}`} style={!ranking.hasScore ? { opacity: 0.55 } : undefined}>
          {scoreDisplay(ranking.score, ranking.hasScore !== false)}
        </div>
        <span className="v2-candidate-name" title={displayName}>{displayName}</span>
        <span className="v2-candidate-meta">#{ranking.rank}</span>
        {reviewAction && (
          <button
            className="v2-btn v2-btn-ghost"
            style={{ fontSize: 'var(--font-size-xs)', padding: '2px 8px', marginLeft: 'auto' }}
            onClick={e => { e.stopPropagation(); reviewAction.onReview(); }}
          >
            Review
          </button>
        )}
        {linkAction && (
          <button
            className={`v2-btn ${linkAction.linked ? 'v2-btn-ghost' : 'v2-btn-secondary'}`}
            style={{ fontSize: 'var(--font-size-xs)', padding: '2px 8px', marginLeft: reviewAction ? 0 : 'auto' }}
            disabled={linkAction.linking || linkAction.linked}
            onClick={e => { e.stopPropagation(); linkAction.onLink(); }}
          >
            {linkAction.linked ? 'Linked' : linkAction.linking ? '...' : '+ Link'}
          </button>
        )}
        <span style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', display: 'flex', marginLeft: linkAction ? 0 : 'auto' }}>
          <IconChevron />
        </span>
      </div>
      {isExpanded && (
        <div className="v2-candidate-detail">
          {ranking.strengths?.length > 0 && (
            <>
              <h4>Strengths</h4>
              <ul>{ranking.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
            </>
          )}
          {ranking.improvements?.length > 0 && (
            <>
              <h4>Areas for Improvement</h4>
              <ul>{ranking.improvements.map((s, i) => <li key={i}>{s}</li>)}</ul>
            </>
          )}
          {ranking.explanation && (
            <>
              <h4>Analysis</h4>
              <p>{ranking.explanation}</p>
            </>
          )}

          {/* Talent Pool link + Enrichment (depth-on-demand) */}
          {ranking.resumeId && (
            <div style={{ marginTop: 'var(--space-3)', borderTop: '1px solid var(--border-color)', paddingTop: 'var(--space-3)', display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
              <button
                className="v2-btn v2-btn-ghost"
                style={{ fontSize: 'var(--font-size-xs)' }}
                onClick={e => { e.stopPropagation(); handleEnrichToggle(); }}
              >
                {enrichOpen ? 'Hide' : 'View'} Enrichment Details
                {enrichLoading && <span className="v2-spinner" style={{ marginLeft: 4 }} />}
              </button>
              <a
                href={`#/talent/${ranking.resumeId}`}
                className="v2-btn v2-btn-ghost"
                style={{ fontSize: 'var(--font-size-xs)', textDecoration: 'none', color: 'var(--color-primary)' }}
                onClick={e => e.stopPropagation()}
              >
                View in Talent Pool →
              </a>
              {unlinkAction && (
                <button
                  className="v2-btn v2-btn-ghost"
                  style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-error, #dc2626)' }}
                  disabled={unlinkAction.unlinking}
                  onClick={e => { e.stopPropagation(); unlinkAction.onUnlink(); }}
                >
                  {unlinkAction.unlinking ? 'Removing...' : 'Unlink from Job'}
                </button>
              )}
              {enrichOpen && enrichment && (() => {
                const resumeText = enrichment.resumeText || '';
                return (
                  <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)', width: '100%' }}>
                    {Object.entries(enrichment.dimensions).map(([dim, data]) => {
                      const meta = DIMENSION_META[dim] || { label: dim, subtitle: '' };
                      return (
                        <div key={dim} style={{ marginTop: 'var(--space-3)', padding: 'var(--space-3)', background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)' }}>
                          <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>{meta.label}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 'var(--space-2)' }}>{meta.subtitle}</div>
                          {(data.skills || data.inferred_skills || []).length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {(data.skills || data.inferred_skills || []).map((s, i) => {
                                const explicit = isSkillExplicitInResume(s, resumeText);
                                const label = formatSkillLabel(s);
                                return (
                                  <span
                                    key={i}
                                    title={explicit ? 'Found in resume' : 'AI-inferred from resume context'}
                                    className={explicit ? 'v2-badge v2-badge-muted' : 'v2-badge'}
                                    style={explicit
                                      ? { fontSize: 11, cursor: 'default' }
                                      : { fontSize: 11, cursor: 'help', background: 'rgba(34,197,94,0.10)', color: 'var(--color-success, #22c55e)', border: '1px solid var(--color-success, #22c55e)' }
                                    }
                                  >
                                    {label}{!explicit && ' ✦'}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                          {data.findings && data.findings.length > 0 && (
                            <div style={{ marginTop: 6, paddingLeft: 2 }}>
                              {data.findings.map((f, fi) => (
                                <div key={fi} style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5, display: 'flex', gap: 4 }}>
                                  <span style={{ opacity: 0.5 }}>•</span><span>{f}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              {enrichOpen && !enrichment && !enrichLoading && (
                <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginTop: 'var(--space-2)' }}>No enrichment data available.</p>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
